import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, FormControl, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { RsbLookupModel } from 'src/app/common/model/rsb-lookup.model';
import { RsbService } from 'src/app/service/rsb.service';
import * as dateUtil from 'src/app/common/utility/date-time.utility';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UploadProgressComponent } from 'src/app/shared/components/upload-progress/upload-progress.component';
import { ImageCropperComponent } from 'src/app/shared/components/image-cropper/image-cropper.component';
import { SearchCountryField, TooltipLabel, CountryISO } from 'ngx-intl-tel-input';
import { forkJoin } from 'rxjs';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
export class SwatchItem {
  swatchName: string = '';
}

/**
 * Custom validator: Ensures at least one checkbox in a group is selected
 * Usage: Apply to FormGroup containing boolean form controls (checkboxes)
 */
function atLeastOneCheckboxSelectedValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    if (!(group instanceof FormGroup)) {
      return null;
    }
    
    const isAnyChecked = Object.keys(group.controls).some(key => {
      return group.get(key)?.value === true;
    });
    
    return isAnyChecked ? null : { atLeastOneCheckbox: true };
  };
}

/**
 * Custom validator: Ensures sqft/area value is within the min-max range
 * Usage: Apply to the sqft/villaArea/plotArea control in a FormArray
 */
function rangeValidator(getMinValue: () => any, getMaxValue: () => any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const value = parseFloat(control.value);
    const minVal = getMinValue();
    const maxVal = getMaxValue();

    if (!minVal || !maxVal) {
      return null;
    }

    const min = parseFloat(minVal);
    const max = parseFloat(maxVal);

    if (value < min || value > max) {
      return { outOfRange: { min, max, actual: value } };
    }

    return null;
  };
}

/**
 * Custom validator: Ensures total units matches the sum of units in configuration rows
 * Usage: Apply to the totalUnits control in Independent House section
 * Requires a function to get the configurations FormArray
 */
function totalUnitsMatchValidator(getConfigurations: () => FormArray): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    try {
      const totalUnits = Number(control.value);
      const configs = getConfigurations();
      
      if (!configs || configs.length === 0) {
        return null;
      }

      // Calculate sum of units from all configuration rows
      let sumOfUnits = 0;
      let hasAnyUnits = false;
      
      configs.controls.forEach((row: any) => {
        const units = row.get('units')?.value;
        if (units) {
          hasAnyUnits = true;
          sumOfUnits += Number(units);
        }
      });

      // Only validate if there's actual units entered in at least one row
      if (!hasAnyUnits) {
        return null;
      }

      // If total units doesn't match sum, return error
      if (totalUnits !== sumOfUnits) {
        return { 
          unitsMismatch: { 
            totalUnits: totalUnits, 
            sumOfUnits: sumOfUnits 
          } 
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  };
}

@Component({
  selector: 'app-configuration',
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.css']
})
export class ConfigurationComponent implements OnInit {
  @Output() showNextPage: EventEmitter<number> = new EventEmitter();
  @Output() formChange: EventEmitter<boolean> = new EventEmitter();
  propertyForm!: FormGroup;
  selectedCompany: any = {};
  selectedProperty: any;
  selectedCountry: any = {};
  selectedModule: any = {};
  selectedCategory: any = {};
  selectedSubCategory: any = {};
  selectedMeasurement: any = {};
  projectStatus: string = 'Ongoing';
  buttonClicked: boolean = false;
  moduleList: Array<any> = [];
  categoryList: Array<any> = [];
  subCatList: Array<any> = [];
  baiscAttrList: Array<any> = [];
  avabhkList: Array<any> = [];
  categoryAttrList: Array<any> = [];
  attributeIds: any[] = [];
  attributePropertyIds: any[] = []
  countryList: Array<any> = [];
  companyList: Array<any> = [];
  measurementList: Array<any> = [];
  notValid: boolean = false;
  vendorDetails: any = {};
  basicAttrValues: any;
  currentDate = dateUtil.convertToPickerDate(new Date());
  propertyPreviewImage!: string;
  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  TooltipLabel = TooltipLabel;
  CountryISO = CountryISO;
  onlyCountries = [];
  loadView: boolean = false;
  activeModel?: NgbActiveModal;
  selectedISOCountry!: CountryISO;
  valid: boolean = false;
  showIndependentConfig: string = '';
  availableBhk: Array<any> = [
    { swatchName: '1', buttonStatus: 'deactive', selected: false },
    { swatchName: '2', buttonStatus: 'deactive', selected: false },
    { swatchName: '3', buttonStatus: 'deactive', selected: false },
    { swatchName: '4', buttonStatus: 'deactive', selected: false },
    { swatchName: '5', buttonStatus: 'deactive', selected: false },
  ];

  titleProperties = [
    {
      id: 1,
      name: 'BEDROOM',
      value: ''
    },
    {
      id: 2,
      name: 'CATEGORY_TYPE',
      value: ''
    },
    {
      id: 3,
      name: 'PROPERTY_TYPE',
      value: ''
    },
    {
      id: 4,
      name: 'MODULE',
      value: ''
    },
    {
      id: 5,
      name: 'LOCATION',
      value: ''
    }
  ];
  firstLoad: boolean = false;
  isFormDirty: boolean = false;
  swatchList: Array<SwatchItem> = [];
  isSubmitted: boolean = false;

  // ✅ Expose global Number function to template
  Number = Number;

  // ✅ CACHED FILTERED INDICES - Cache results to prevent constant re-filtering
  cachedConfigIndices: number[] | null = null;
  cachedTowerConfigIndices: Map<number, number[]> = new Map();
  cachedPhaseConfigIndices: Map<number, number[]> = new Map();
  cachedPlotConfigIndices: Map<number, number[]> = new Map();
  cachedVentureConfigIndices: Map<number, number[]> = new Map();

  constructor(
    private fb: FormBuilder,
    private lookupModel: RsbLookupModel,
    private rsbService: RsbService,
    private modalService: NgbModal,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.isSubmitted = false;
    this.firstLoad = true;
    this.selectedProperty = this.lookupModel.getVendorProperty() ? this.lookupModel.getVendorProperty() : null;
    this.selectedCountry = this.lookupModel.getPropertyCountry() ? this.lookupModel.getPropertyCountry() : {};
    this.selectedModule = this.lookupModel.getModule();
    console.log('Selected Property:', this.selectedProperty);
    this.propertyForm = this.fb.group({
      // ✅ REQUIRED: Module, Category, SubCategory
      moduleId: [this.selectedProperty?.moduleId ? this.selectedProperty?.moduleId : this.selectedModule?.moduleId, 
        [Validators.required]],
      categoryId: [this.selectedProperty?.categoryId ? this.selectedProperty?.categoryId : '', 
        [Validators.required]],
      subCategoryId: [this.selectedProperty?.subCategoryId ? this.selectedProperty?.subCategoryId : '', 
        [Validators.required]],
      companyId: ['', [Validators.required]],
      
      // ✅ Project Information - Made optional for now
      projectName: [this.selectedProperty?.projectName ? this.selectedProperty.projectName : '', 
        [Validators.minLength(3), Validators.maxLength(100)]],
      projectDescription: [this.selectedProperty?.projectDescription ? this.selectedProperty.projectDescription : '', 
        [Validators.minLength(10), Validators.maxLength(1000)]],
      
      // ✅ Independent House Fields - Initialize numeric fields with null for proper number input binding
      totalUnits: [null, 
        [Validators.required, Validators.min(1), Validators.max(10000), Validators.pattern(/^\d+$/)]],
      minArea: [null, 
        [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      maxArea: [null, 
        [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      structure: ['', [Validators.required]],
      
      // ✅ Facing Options
      facing: this.fb.group({
        east: [false],
        west: [false],
        north: [false],
        south: [false]
      }, { validators: atLeastOneCheckboxSelectedValidator() }),

      // ✅ Dynamic Config Arrays - Start EMPTY, let each subcategory initialize them
      configurations: this.fb.array([]),
      towers: this.fb.array([]),
      phases: this.fb.array([]),
      plotPhases: this.fb.array([]),
      venturePhases: this.fb.array([])
    }
    );
    
    // ✅ SET UP CACHE LISTENERS - Update cached indices when form values change
    this.setupCacheListeners();
    
    this.getModules();
    this.getCountryList();
    this.getBuilderCompanies();
  }

  /**
   * Select the first company by default
   */
  selectFirstCompany(): void {
    if (this.companyList && this.companyList.length > 0) {
      this.changeCompany(this.companyList[0]);
    }
  }

  /**
   * Set up cache listeners to update cached indices when form values change
   */
  setupCacheListeners(): void {
    // Update Independent House cache when facing or area changes
    this.propertyForm.get('facing')?.valueChanges.subscribe(() => {
      this.updateCachedConfigIndices();
    });
    this.propertyForm.get('minArea')?.valueChanges.subscribe(() => {
      this.updateCachedConfigIndices();
    });
    this.propertyForm.get('maxArea')?.valueChanges.subscribe(() => {
      this.updateCachedConfigIndices();
    });

    // Initialize cache
    this.updateCachedConfigIndices();
  }

  /**
   * Get builder companies list
   */
  getBuilderCompanies(): void {
    const companyId: string = this.lookupModel.getCompanyId() ? this.lookupModel.getCompanyId() : this.selectedProperty && this.selectedProperty.companyId ? this.selectedProperty.companyId : '';
    this.rsbService.getBuilderCompanies({}).subscribe((rsp) => {
      if (rsp.statusCode === 0 && rsp.contents.length) {
        this.companyList = rsp.contents;
        if (companyId) {
          this.companyList.map((obj) => {
            if (obj.companyId === companyId) {
              obj["selected"] = true;
            } else {
              obj["selected"] = false;
            }
          })
          this.propertyForm.controls['companyId'].setValue(companyId);
        } else {
          // Select first company by default if no companyId is set
          this.selectFirstCompany();
        }
      }
    })
  }

  /**
   * Navigate to add company page
   */
  navigateToAddCompany(): void {
    this.router.navigate(['/addCompany/add']);
  }



  createConfigRow(): FormGroup {
    return this.fb.group({
      // ✅ FACING: Optional here, validated on submit
      facing: [''],
      
      // ✅ BHK: Optional here, validated on submit
      bhk: [''],
      
      // ✅ SQFT: Optional number field, validated on submit
      sqft: [null, [Validators.min(1), Validators.max(100000)]],
      
      // ✅ PRICE: Optional number field, validated on submit
      price: [null, [Validators.min(50000), Validators.max(10000000)]],
      
      // ✅ UNITS: Optional number field, validated on submit
      units: [null, [Validators.min(1), Validators.max(1000)]],
      
      // ✅ AVAILABLE: Optional number field, validated on submit
      available: [null, [Validators.min(0), Validators.max(999)]]
    });
  }

  createVentureRow(): FormGroup {
    return this.fb.group({
      // ✅ FACING: Optional
      facing: [''],
      
      // ✅ VILLA AREA: Optional number field
      villaArea: [null, [Validators.min(1)]],
      
      // ✅ BHK: Optional
      bhk: [''],
      
      // ✅ SQ FEET: Optional number field
      sqFeet: [null, [Validators.min(1), Validators.max(100000)]],
      
      // ✅ SQ FEET PRICE: Optional number field
      sqFeetPrice: [null, [Validators.min(100), Validators.max(500000)]],
      
      // ✅ UNIT PRICE: Optional number field
      unitPrice: [null, [Validators.min(50000), Validators.max(10000000)]],
      
      // ✅ UNITS: Optional number field
      units: [null, [Validators.min(0), Validators.max(1000)]]
    });
  }

  clearValidators() {
    Object.keys(this.propertyForm.controls).forEach(key => {
      const control = this.propertyForm.get(key);
      control?.clearValidators();
      control?.updateValueAndValidity();
    });
  }

  createPlotRow(): FormGroup {
    return this.fb.group({
      // ✅ FACING: Optional
      facing: [''],
      
      // ✅ PLOT AREA: Optional number field
      plotArea: [null, [Validators.min(1)]],
      
      // ✅ SQFT PRICE: Optional number field
      sqftPrice: [null, [Validators.min(100), Validators.max(500000)]],
      
      // ✅ UNIT PRICE: Optional number field
      unitPrice: [null, [Validators.min(50000), Validators.max(10000000)]],
      
      // ✅ UNITS: Optional number field
      units: [null, [Validators.min(1), Validators.max(1000)]],
      
      // ✅ AVAILABLE: Optional number field
      available: [null, [Validators.min(0), Validators.max(999)]]
    });
  }

  get venturePhases(): FormArray {
    return this.propertyForm.get('venturePhases') as FormArray;
  }

  get configurations(): FormArray {
    return this.propertyForm.get('configurations') as FormArray;
  }

  createVenturePhase(name = ''): FormGroup {
    return this.fb.group({
      // ✅ NAME: Required, length validation
      name: [name, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      isEditing: [false],
      
      // ✅ TOTAL UNITS: Required, positive integer - Initialize with null for number field
      totalUnits: [null, [Validators.required, Validators.min(1), Validators.max(10000), Validators.pattern(/^\d+$/)]],
      
      // ✅ PLOT SIZE UNIT: Not required at form level - validated in validateVenture method
      plotSizeUnit: ['', []],
      
      // ✅ LENGTH: Required, positive number - Initialize with null for number field
      length: [null, [Validators.required, Validators.min(10), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ WIDTH: Required, positive number - Initialize with null for number field
      width: [null, [Validators.required, Validators.min(10), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ MIN AREA: Required, positive number - Initialize with null for number field
      minArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ MAX AREA: Required, positive number, must be > minArea - Initialize with null for number field
      maxArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ TOTAL AREA: Required, positive number - Initialize with null for number field
      totalArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ STRUCTURE: Group of checkboxes - Initialize villa to true by default
      structure: this.fb.group({
        villa: [true],
        duplex: [false],
        triplex: [false]
      }, { validators: atLeastOneCheckboxSelectedValidator() }),

      // ✅ FACING: Group of checkboxes - Initialize east to true by default
      facing: this.fb.group({
        east: [true],
        west: [false],
        north: [false],
        south: [false]
      }, { validators: atLeastOneCheckboxSelectedValidator() }),

      // ✅ START EMPTY: Let initialization methods add rows
      configurations: this.fb.array([])
    });
  }

  addVenturePhase() {
    const count = this.venturePhases.length + 1;
    this.venturePhases.push(this.createVenturePhase('Phase ' + count));
    // ✅ Add default configuration row to new venture phase
    const newVenturePhaseIndex = this.venturePhases.length - 1;
    this.addConfigRowToVenture(newVenturePhaseIndex);
    
    // ✅ SET UP VALIDATORS for the newly added venture phase
    this.setupVenturePhaseValidators(newVenturePhaseIndex);
  }

  removeVenturePhase(index: number) {
    this.venturePhases.removeAt(index);
  }
  getTowerForm(index: number): FormGroup {
    return this.towers.at(index) as FormGroup;
  }

  getPhaseForm(index: number): FormGroup {
    return this.phases.at(index) as FormGroup;
  }

  getPlotPhaseForm(index: number): FormGroup {
    return this.plotPhases.at(index) as FormGroup;
  }

  getVenturePhaseForm(index: number): FormGroup {
    return this.venturePhases.at(index) as FormGroup;
  }

  get phases(): FormArray {
    return this.propertyForm.get('phases') as FormArray;
  }

  getVentureConfigs(index: number): FormArray {
    return this.venturePhases.at(index).get('configurations') as FormArray;
  }

  addConfigRowToVenture(index: number) {
    this.getVentureConfigs(index).push(this.createVentureRow());
    this.updateCachedVentureIndices(index);
  }

  removeConfigRowFromVenture(index: number, i: number) {
    if (this.getVentureConfigs(index).length > 1) {
      this.getVentureConfigs(index).removeAt(i);
      this.updateCachedVentureIndices(index);
    }
  }

  createPhase(name = ''): FormGroup {
    return this.fb.group({
      // ✅ NAME: Required, length validation
      name: [name, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],

      // ✅ TOTAL UNITS: Required, positive integer - Initialize with null for number field
      totalUnits: [null, [Validators.required, Validators.min(1), Validators.max(10000), Validators.pattern(/^\d+$/)]],
      
      // ✅ MIN AREA: Required, positive, reasonable minimum - Initialize with null for number field
      minArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ MAX AREA: Required, positive, must be > minArea - Initialize with null for number field
      maxArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ STRUCTURE: Group of checkboxes (at least one should be selected)
      structure: this.fb.group({
        villa: [true],
        duplex: [false],
        triplex: [false]
      }, { validators: atLeastOneCheckboxSelectedValidator() }),

      // ✅ FACING: Group of checkboxes (at least one should be selected)
      facing: this.fb.group({
        east: [true],
        west: [false],
        north: [false],
        south: [false]
      }, { validators: atLeastOneCheckboxSelectedValidator() }),

      // ✅ START EMPTY: Let initialization methods add rows
      configurations: this.fb.array([])
    });
  }

  addPhase() {
    const count = this.phases.length + 1;
    this.phases.push(this.createPhase('Phase ' + count));
    // ✅ Add default configuration row to new phase
    const newPhaseIndex = this.phases.length - 1;
    this.addConfigRowToPhase(newPhaseIndex);
    
    // ✅ SET UP VALIDATORS for the newly added phase
    this.setupVillaPhaseValidators(newPhaseIndex);
  }

  removePhase(index: number) {
    this.phases.removeAt(index);
  }

  getPhaseConfigurations(pIndex: number): FormArray {
    return this.phases.at(pIndex).get('configurations') as FormArray;
  }

  addConfigRowToPhase(pIndex: number) {
    this.getPhaseConfigurations(pIndex).push(this.createConfigRow());
    this.updateCachedPhaseIndices(pIndex);
  }

  removeConfigRowFromPhase(pIndex: number, i: number) {
    if (this.getPhaseConfigurations(pIndex).length > 1) {
      this.getPhaseConfigurations(pIndex).removeAt(i);
      this.updateCachedPhaseIndices(pIndex);
    }
  }


  get plotPhases(): FormArray {
    return this.propertyForm.get('plotPhases') as FormArray;
  }

  createPlotPhase(name = ''): FormGroup {
    return this.fb.group({
      // ✅ NAME: Required, length validation
      name: [name, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      isEditing: [false],

      // ✅ TOTAL UNITS: Required, positive integer - Initialize with null for number field
      totalUnits: [null, [Validators.required, Validators.min(1), Validators.max(10000), Validators.pattern(/^\d+$/)]],
      
      // ✅ TOTAL AREA: Required, positive number - Initialize with null for number field
      totalArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ PLOT SIZE UNIT: Not required at form level - validated in validatePlot method
      plotSizeUnit: ['', []],
      
      // ✅ MIN SIZE: Required, positive number - Initialize with null for number field
      minSize: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ MAX SIZE: Required, positive number, must be > minSize - Initialize with null for number field
      maxSize: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ STRUCTURE: Group of checkboxes
      structure: this.fb.group({
        villa: [true],
        duplex: [false],
        triplex: [false]
      }, { validators: atLeastOneCheckboxSelectedValidator() }),
      
      // ✅ LENGTH: Required, positive number - Initialize with null for number field
      length: [null, [Validators.required, Validators.min(10), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ WIDTH: Required, positive number - Initialize with null for number field
      width: [null, [Validators.required, Validators.min(10), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],

      // ✅ FACING: Group of checkboxes
      facing: this.fb.group({
        east: [true],
        west: [false],
        north: [false],
        south: [false]
      }, { validators: atLeastOneCheckboxSelectedValidator() }),

      // ✅ START EMPTY: Let initialization methods add rows
      configurations: this.fb.array([])
    });
  }

  addPlotPhase() {
    const count = this.plotPhases.length + 1;
    this.plotPhases.push(this.createPlotPhase('Phase ' + count));
    // ✅ Add default configuration row to new plot phase
    const newPlotPhaseIndex = this.plotPhases.length - 1;
    this.addConfigRowToPlot(newPlotPhaseIndex);
    
    // ✅ SET UP VALIDATORS for the newly added plot phase
    this.setupPlotPhaseValidators(newPlotPhaseIndex);
  }

  removePlotPhase(index: number) {
    this.plotPhases.removeAt(index);
  }

  getPlotConfigurations(index: number): FormArray {
    return this.plotPhases.at(index).get('configurations') as FormArray;
  }

  addConfigRowToPlot(index: number) {
    this.getPlotConfigurations(index).push(this.createPlotRow());
    this.updateCachedPlotIndices(index);
  }

  removeConfigRowFromPlot(index: number, i: number) {
    if (this.getPlotConfigurations(index).length > 1) {
      this.getPlotConfigurations(index).removeAt(i);
      this.updateCachedPlotIndices(index);
    }
  }

  onlyDigits(event: any): boolean {
    const charcode = (event.which) ? event.which : event.KeyCode;
    if (charcode > 31 && (charcode < 48 || charcode > 57)) {
      return false;
    }
    return true;
  }

  checkReadOnly(): boolean {
    const readonly: boolean = this.selectedProperty && ([1, 2, 3].includes(this.selectedProperty.status));
    return readonly;
  }

  addSwatch(): void {
    const newswatch = new SwatchItem();
    this.swatchList.push(newswatch);
  }

  deleteSwatch(index: number): void {
    this.swatchList.splice(index, 1);
  }
  selectAttribute(prop: any, attributeProperties: Array<any>, attribute: any) {
    const obj: any = {};
    if (attribute.displayType === 1 || attribute.displayType === 3) {
      if (prop.selected && prop.selected === true) {
        prop.selected = false
      } else {
        attributeProperties.map((obj: any, index) => {
          if (obj.selected === true) {
            obj.selected = false
          }
        })
        obj["attributeId"] = attribute.attributeId;
        obj["attrValue"] = prop.attributePropertyId
        prop.selected = true;
      }
    } else {
      if (prop.selected && prop.selected === true) {
        prop.selected = false;
      } else {
        prop.selected = true;
      }

    }
    // const values:Array<any> = this.basicAttributess.controls.map(control => control.value);
    // values.map((obj) => {
    //    if(obj.attributeId === attribute.attributeId) {
    //       obj.attrValue = prop.propertyId
    //    }
    // })

    // const controlArray = this.basicAttributess as FormArray;
  }

  get basicAttributess(): FormArray {
    return this.propertyForm.get('basicAttributes') as FormArray;
  }

  // ✅ VALIDATION HELPER METHODS
  getFieldErrors(fieldName: string): any {
    const field = this.propertyForm.get(fieldName);
    return (field?.touched || field?.dirty || this.isSubmitted) ? field?.errors : null;
  }

  hasError(fieldName: string, errorType: string): boolean {
    const field = this.propertyForm.get(fieldName);
    return (field?.touched || field?.dirty || this.isSubmitted) && field?.errors?.[errorType];
  }

  // ✅ GET ERROR MESSAGE FOR A FIELD
  getErrorMessage(fieldName: string): string {
    const field = this.propertyForm.get(fieldName);
    if (!field?.errors) return '';

    if (field.errors['required']) return `${this.formatFieldName(fieldName)} is required`;
    if (field.errors['minlength']) return `${this.formatFieldName(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
    if (field.errors['maxlength']) return `${this.formatFieldName(fieldName)} cannot exceed ${field.errors['maxlength'].requiredLength} characters`;
    if (field.errors['min']) return `${this.formatFieldName(fieldName)} must be at least ${field.errors['min'].min}`;
    if (field.errors['max']) return `${this.formatFieldName(fieldName)} cannot exceed ${field.errors['max'].max}`;
    if (field.errors['pattern']) return `${this.formatFieldName(fieldName)} format is invalid`;

    return 'Invalid input';
  }

  // ✅ FORMAT FIELD NAME FOR DISPLAY
  formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  // ✅ GETTER METHODS FOR FORM CONTROLS (For easy access in template)
  get totalUnits() { return this.propertyForm.get('totalUnits')!; }
  get minArea() { return this.propertyForm.get('minArea')!; }
  get maxArea() { return this.propertyForm.get('maxArea')!; }
  get structure() { return this.propertyForm.get('structure')!; }

  // ✅ ON CONFIG CHANGE - For tracking changes in configuration rows
  onConfigChange(index: number, sqftValue?: any): void {
    this.isFormDirty = true;
    this.formChange.emit(true);
    
    const config = this.configurations.at(index) as FormGroup;
    const sqftControl = config.get('sqft');
    
    console.log('=== onConfigChange DEBUG ===');
    console.log('Index:', index);
    console.log('Total configurations in FormArray:', this.configurations.length);
    console.log('Visible row indices (cached):', this.getCachedConfigIndices());
    console.log('Current row data:', config.getRawValue());
    console.log('Passed sqftValue:', sqftValue);
    console.log('Form control value:', sqftControl?.value);
    
    // If value was passed but form control is empty, set it
    if (sqftValue !== undefined && sqftValue !== null && sqftValue !== '') {
      console.log('Setting form control value to:', sqftValue);
      sqftControl?.setValue(Number(sqftValue), { emitEvent: false });
    }
    
    sqftControl?.markAsTouched();
    sqftControl?.updateValueAndValidity();
    
    console.log('After update - Form control value:', sqftControl?.value);
    console.log('After update - Form control errors:', sqftControl?.errors);
    console.log('After update - Form control valid:', sqftControl?.valid);
    
    // Update cache so row stays visible even with new sqft value
    this.updateCachedConfigIndices();
  }

  // ✅ GET SELECTED FACING OPTIONS - For Independent House
  getSelectedFacings(): string[] {
    const facingControl = this.propertyForm.get('facing');
    const selectedFacings = [];
    if (facingControl?.get('east')?.value) selectedFacings.push('East');
    if (facingControl?.get('west')?.value) selectedFacings.push('West');
    if (facingControl?.get('north')?.value) selectedFacings.push('North');
    if (facingControl?.get('south')?.value) selectedFacings.push('South');
    return selectedFacings;
  }

  // ✅ GET SELECTED FACING OPTIONS FOR TOWER
  getSelectedTowerFacings(tIndex: number): string[] {
    const tower = this.towers.at(tIndex) as FormGroup;
    const facingControl = tower.get('facing');
    const selectedFacings = [];
    if (facingControl?.get('east')?.value) selectedFacings.push('East');
    if (facingControl?.get('west')?.value) selectedFacings.push('West');
    if (facingControl?.get('north')?.value) selectedFacings.push('North');
    if (facingControl?.get('south')?.value) selectedFacings.push('South');
    return selectedFacings;
  }

  // ✅ GET SELECTED FACING OPTIONS FOR PHASE
  getSelectedPhaseFacings(pIndex: number): string[] {
    const phase = this.phases.at(pIndex) as FormGroup;
    const facingControl = phase.get('facing');
    const selectedFacings = [];
    if (facingControl?.get('east')?.value) selectedFacings.push('East');
    if (facingControl?.get('west')?.value) selectedFacings.push('West');
    if (facingControl?.get('north')?.value) selectedFacings.push('North');
    if (facingControl?.get('south')?.value) selectedFacings.push('South');
    return selectedFacings;
  }

  // ✅ GET SELECTED FACING OPTIONS FOR PLOT PHASE
  getSelectedPlotFacings(pIndex: number): string[] {
    const plotPhase = this.plotPhases.at(pIndex) as FormGroup;
    const facingControl = plotPhase.get('facing');
    const selectedFacings = [];
    if (facingControl?.get('east')?.value) selectedFacings.push('East');
    if (facingControl?.get('west')?.value) selectedFacings.push('West');
    if (facingControl?.get('north')?.value) selectedFacings.push('North');
    if (facingControl?.get('south')?.value) selectedFacings.push('South');
    return selectedFacings;
  }

  // ✅ GET SELECTED FACING OPTIONS FOR VENTURE PHASE
  getSelectedVentureFacings(vIndex: number): string[] {
    const venture = this.venturePhases.at(vIndex) as FormGroup;
    const facingControl = venture.get('facing');
    const selectedFacings = [];
    if (facingControl?.get('east')?.value) selectedFacings.push('East');
    if (facingControl?.get('west')?.value) selectedFacings.push('West');
    if (facingControl?.get('north')?.value) selectedFacings.push('North');
    if (facingControl?.get('south')?.value) selectedFacings.push('South');
    return selectedFacings;
  }

  // ✅ GET AVAILABLE FACING OPTIONS - For Independent House
  // Returns selected facings if any are checked, otherwise returns all options
  getAvailableFacings(): string[] {
    const selectedFacings = this.getSelectedFacings();
    if (selectedFacings.length > 0) {
      return selectedFacings;
    }
    return ['East', 'West', 'North', 'South'];
  }

  // ✅ GET AVAILABLE FACING OPTIONS - For Tower
  getAvailableTowerFacings(tIndex: number): string[] {
    const selectedFacings = this.getSelectedTowerFacings(tIndex);
    if (selectedFacings.length > 0) {
      return selectedFacings;
    }
    return ['East', 'West', 'North', 'South'];
  }

  // ✅ GET AVAILABLE FACING OPTIONS - For Phase
  getAvailablePhaseFacings(pIndex: number): string[] {
    const selectedFacings = this.getSelectedPhaseFacings(pIndex);
    if (selectedFacings.length > 0) {
      return selectedFacings;
    }
    return ['East', 'West', 'North', 'South'];
  }

  // ✅ GET AVAILABLE FACING OPTIONS - For Plot Phase
  getAvailablePlotFacings(pIndex: number): string[] {
    const selectedFacings = this.getSelectedPlotFacings(pIndex);
    if (selectedFacings.length > 0) {
      return selectedFacings;
    }
    return ['East', 'West', 'North', 'South'];
  }

  // ✅ GET AVAILABLE FACING OPTIONS - For Venture Phase
  getAvailableVentureFacings(vIndex: number): string[] {
    const selectedFacings = this.getSelectedVentureFacings(vIndex);
    if (selectedFacings.length > 0) {
      return selectedFacings;
    }
    return ['East', 'West', 'North', 'South'];
  }

  // ✅ NEW METHOD: Calculate the sum of units in all configuration rows
  getSumOfConfigurationUnits(): number {
    let sum = 0;
    this.configurations.controls.forEach((row: any) => {
      const units = row.get('units')?.value;
      // Count all units that have a value (including the default row)
      if (units) {
        sum += Number(units);
      }
    });
    return sum;
  }

  // ✅ NEW METHOD: Check if total units matches the sum of configuration units
  doUnitsMatch(): boolean {
    const totalUnits = this.propertyForm.get('totalUnits')?.value;
    const sumOfUnits = this.getSumOfConfigurationUnits();
    return totalUnits && Number(totalUnits) === sumOfUnits;
  }

  // ✅ CHECK IF SQFT IS IN RANGE - For Independent House
  isConfigSqftInRange(index: number): boolean {
    const config = this.configurations.at(index) as FormGroup;
    const sqft = config.get('sqft')?.value;
    const minArea = this.propertyForm.get('minArea')?.value;
    const maxArea = this.propertyForm.get('maxArea')?.value;

    // If any required value is missing or empty, don't validate (return true = valid)
    if (!sqft || !minArea || !maxArea) {
      return true;
    }
    
    const val = parseFloat(String(sqft));
    const min = parseFloat(String(minArea));
    const max = parseFloat(String(maxArea));

    // If parsing failed, consider it valid to avoid false positives
    if (isNaN(val) || isNaN(min) || isNaN(max)) {
      return true;
    }

    return val >= min && val <= max;
  }

  // ✅ CHECK IF SQFT IS IN RANGE - For Tower
  isTowerConfigSqftInRange(tIndex: number, index: number): boolean {
    const tower = this.towers.at(tIndex) as FormGroup;
    const configs = tower.get('configurations') as FormArray;
    const config = configs.at(index) as FormGroup;
    const sqft = config.get('sqft')?.value;
    const minArea = tower.get('minArea')?.value;
    const maxArea = tower.get('maxArea')?.value;

    // If any required value is missing or empty, don't validate (return true = valid)
    if (!sqft || !minArea || !maxArea) {
      return true;
    }
    
    const val = parseFloat(String(sqft));
    const min = parseFloat(String(minArea));
    const max = parseFloat(String(maxArea));

    // If parsing failed, consider it valid to avoid false positives
    if (isNaN(val) || isNaN(min) || isNaN(max)) {
      return true;
    }

    return val >= min && val <= max;
  }

  // ✅ CHECK IF SQFT IS IN RANGE - For Phase
  isPhaseConfigSqftInRange(pIndex: number, index: number): boolean {
    const phase = this.phases.at(pIndex) as FormGroup;
    const configs = phase.get('configurations') as FormArray;
    const config = configs.at(index) as FormGroup;
    const sqft = config.get('sqft')?.value;
    const minArea = phase.get('minArea')?.value;
    const maxArea = phase.get('maxArea')?.value;

    // If any required value is missing or empty, don't validate (return true = valid)
    if (!sqft || !minArea || !maxArea) {
      return true;
    }
    
    const val = parseFloat(String(sqft));
    const min = parseFloat(String(minArea));
    const max = parseFloat(String(maxArea));

    // If parsing failed, consider it valid to avoid false positives
    if (isNaN(val) || isNaN(min) || isNaN(max)) {
      return true;
    }

    return val >= min && val <= max;
  }

  // ✅ CHECK IF PLOT AREA IS IN RANGE - For Plot Phase
  isPlotConfigAreaInRange(pIndex: number, index: number): boolean {
    const plotPhase = this.plotPhases.at(pIndex) as FormGroup;
    const configs = plotPhase.get('configurations') as FormArray;
    const config = configs.at(index) as FormGroup;
    const plotArea = config.get('plotArea')?.value;
    const minSize = plotPhase.get('minSize')?.value;
    const maxSize = plotPhase.get('maxSize')?.value;

    // If any required value is missing or empty, don't validate (return true = valid)
    if (!plotArea || !minSize || !maxSize) {
      return true;
    }
    
    const val = parseFloat(String(plotArea));
    const min = parseFloat(String(minSize));
    const max = parseFloat(String(maxSize));

    // If parsing failed, consider it valid to avoid false positives
    if (isNaN(val) || isNaN(min) || isNaN(max)) {
      return true;
    }

    return val >= min && val <= max;
  }

  // ✅ CHECK IF VILLA AREA IS IN RANGE - For Venture Phase
  isVentureConfigAreaInRange(vIndex: number, index: number): boolean {
    const venture = this.venturePhases.at(vIndex) as FormGroup;
    const configs = venture.get('configurations') as FormArray;
    const config = configs.at(index) as FormGroup;
    const villaArea = config.get('villaArea')?.value;
    const minArea = venture.get('minArea')?.value;
    const maxArea = venture.get('maxArea')?.value;

    // Only validate if villaArea has a value
    if (villaArea === null || villaArea === undefined || villaArea === '' || villaArea === 0) {
      return true;
    }
    
    // Only validate if both min and max are defined
    if (minArea === null || minArea === undefined || minArea === '' ||
        maxArea === null || maxArea === undefined || maxArea === '') {
      return true;
    }
    
    const val = parseFloat(String(villaArea));
    const min = parseFloat(String(minArea));
    const max = parseFloat(String(maxArea));

    // If parsing failed, can't validate
    if (isNaN(val) || isNaN(min) || isNaN(max)) {
      return true;
    }

    return val >= min && val <= max;
  }

  // ✅ GET FILTERED CONFIGURATIONS - Filter based on selected facing and sqft range
  getFilteredConfigurations(): any[] {
    const configs = this.configurations.controls;
    const facingControl = this.propertyForm.get('facing');
    const minArea = this.propertyForm.get('minArea')?.value;
    const maxArea = this.propertyForm.get('maxArea')?.value;

    // Get selected facing options
    const selectedFacings: string[] = [];
    if (facingControl?.get('east')?.value) selectedFacings.push('East');
    if (facingControl?.get('west')?.value) selectedFacings.push('West');
    if (facingControl?.get('north')?.value) selectedFacings.push('North');
    if (facingControl?.get('south')?.value) selectedFacings.push('South');

    // Filter configurations
    return configs.filter((config: any, index: number) => {
      const facing = config.get('facing')?.value;
      const sqft = config.get('sqft')?.value;
      const bhk = config.get('bhk')?.value;

      // Check if facing is selected
      const facingMatch = selectedFacings.length === 0 || selectedFacings.includes(facing);
      
      // Check if sqft is within range
      const sqftMatch = !minArea || !maxArea || (sqft >= minArea && sqft <= maxArea);

      // Return true if both conditions match
      return facingMatch && sqftMatch;
    });
  }

  // ✅ GET FILTERED CONFIGURATION INDICES - Get indices of filtered configurations
  getFilteredConfigurationIndices(): number[] {
    const configs = this.configurations.controls;
    console.log('getFilteredConfigurationIndices() called, number of controls:', configs.length);
    
    // ✅ For Independent House, we show ALL rows regardless of facing selection
    // The facing checkboxes only control which facing options are available in the dropdown
    // They don't filter which rows are visible
    const indices: number[] = [];
    
    configs.forEach((config: any, index: number) => {
      indices.push(index);
    });

    console.log('Computed indices:', indices);

    // ✅ Ensure we always have at least one row visible
    if (indices.length === 0 && configs.length > 0) {
      indices.push(0);
    }

    console.log('Final indices to return:', indices);
    return indices;
  }

  // ✅ GET FILTERED TOWER CONFIGURATIONS - Show all rows
  getFilteredTowerConfigurations(tIndex: number): number[] {
    const tower = this.towers.at(tIndex) as FormGroup;
    const configs = tower.get('configurations') as FormArray;

    // ✅ For Towers, we show ALL rows regardless of facing selection
    // The facing checkboxes only control which facing options are available in the dropdown
    const indices: number[] = [];
    
    configs.controls.forEach((config: any, index: number) => {
      indices.push(index);
    });

    // ✅ Ensure we always have at least one row visible
    if (indices.length === 0 && configs.length > 0) {
      indices.push(0);
    }

    return indices;
  }

  // ✅ GET FILTERED PHASE CONFIGURATIONS - Show all rows
  getFilteredPhaseConfigurations(pIndex: number): number[] {
    const phase = this.phases.at(pIndex) as FormGroup;
    const configs = phase.get('configurations') as FormArray;

    // ✅ For Phases, we show ALL rows regardless of facing selection
    // The facing checkboxes only control which facing options are available in the dropdown
    const indices: number[] = [];
    
    configs.controls.forEach((config: any, index: number) => {
      indices.push(index);
    });

    // ✅ Ensure we always have at least one row visible
    if (indices.length === 0 && configs.length > 0) {
      indices.push(0);
    }

    return indices;
  }

  // ✅ GET FILTERED PLOT CONFIGURATIONS - Show all rows
  getFilteredPlotConfigurations(pIndex: number): number[] {
    const plotPhase = this.plotPhases.at(pIndex) as FormGroup;
    const configs = plotPhase.get('configurations') as FormArray;

    // ✅ For Plot Phases, we show ALL rows regardless of facing selection
    // The facing checkboxes only control which facing options are available in the dropdown
    const indices: number[] = [];
    
    configs.controls.forEach((config: any, index: number) => {
      indices.push(index);
    });

    // ✅ Ensure we always have at least one row visible
    if (indices.length === 0 && configs.length > 0) {
      indices.push(0);
    }

    return indices;
  }

  // ✅ GET FILTERED VENTURE CONFIGURATIONS - Show all rows
  getFilteredVentureConfigurations(vIndex: number): number[] {
    const venture = this.venturePhases.at(vIndex) as FormGroup;
    const configs = venture.get('configurations') as FormArray;

    // ✅ For Venture Phases, we show ALL rows regardless of facing selection
    // The facing checkboxes only control which facing options are available in the dropdown
    const indices: number[] = [];
    
    configs.controls.forEach((config: any, index: number) => {
      indices.push(index);
    });

    // ✅ Ensure we always have at least one row visible
    if (indices.length === 0 && configs.length > 0) {
      indices.push(0);
    }

    return indices;
  }

  // ✅ UPDATE CACHED INDICES - Called when facing or area values change
  updateCachedConfigIndices(): void {
    const filtered = this.getFilteredConfigurationIndices();
    console.log('updateCachedConfigIndices() called, setting to:', filtered);
    this.cachedConfigIndices = filtered;
    console.log('cachedConfigIndices after update:', this.cachedConfigIndices);
  }

  updateCachedTowerIndices(tIndex: number): void {
    this.cachedTowerConfigIndices.set(tIndex, this.getFilteredTowerConfigurations(tIndex));
  }

  updateCachedPhaseIndices(pIndex: number): void {
    this.cachedPhaseConfigIndices.set(pIndex, this.getFilteredPhaseConfigurations(pIndex));
  }

  updateCachedPlotIndices(pIndex: number): void {
    this.cachedPlotConfigIndices.set(pIndex, this.getFilteredPlotConfigurations(pIndex));
  }

  updateCachedVentureIndices(vIndex: number): void {
    this.cachedVentureConfigIndices.set(vIndex, this.getFilteredVentureConfigurations(vIndex));
  }

  // ✅ GET CACHED FILTERED INDICES - Return cached results instead of recalculating
  getCachedConfigIndices(): number[] {
    const result = this.cachedConfigIndices !== null ? this.cachedConfigIndices : this.getFilteredConfigurationIndices();
    console.log('getCachedConfigIndices() called, returning:', result);
    return result;
  }

  getCachedTowerIndices(tIndex: number): number[] {
    return this.cachedTowerConfigIndices.get(tIndex) || this.getFilteredTowerConfigurations(tIndex);
  }

  getCachedPhaseIndices(pIndex: number): number[] {
    return this.cachedPhaseConfigIndices.get(pIndex) || this.getFilteredPhaseConfigurations(pIndex);
  }

  getCachedPlotIndices(pIndex: number): number[] {
    return this.cachedPlotConfigIndices.get(pIndex) || this.getFilteredPlotConfigurations(pIndex);
  }

  getCachedVentureIndices(vIndex: number): number[] {
    return this.cachedVentureConfigIndices.get(vIndex) || this.getFilteredVentureConfigurations(vIndex);
  }

  filterBasicPropertyId(): any[] {
    this.attributePropertyIds = [];
    this.baiscAttrList.forEach(prop => {
      if (prop.displayType != 4) {
        prop.attributeProperties.forEach((prop1: any) => {
          if (prop1.selected === true) {
            this.attributeIds.push(prop?.attributeId);
            this.attributePropertyIds.push(prop1.propertyId);
          }

        });
      }
    })
    return this.attributePropertyIds;
  }

  prepareActiveButton(basicAttrListt: Array<any>): void {
    this.attributeIds = [];
    let attId: any;
    basicAttrListt.forEach(prop => {
      this.attributeIds.push(prop.attributeId);
      if (prop.displayType === 1 || prop.displayType === 2 || prop.displayType === 3) {
        prop.attributeProperties.forEach((prop1: any) => {
          attId = prop1.propertyId;
          const index = this.attributePropertyIds.findIndex((prop2: any) => prop2 === attId);
          if (index != -1) {
            prop1.selected = true;
          } else {
            prop1.selected = false;
          }

        });
      }
    })


  }
  changeMeasurement(measurement: any) {

    this.selectedMeasurement = measurement;
    this.measurementList.map((obj, index) => {
      if (obj.measurementId === measurement.measurementId) {
        obj["selected"] = true;
      } else {
        obj["selected"] = false;
      }
    })
    this.propertyForm.controls['measurement'].setValue(measurement.measurementId);
  }
  onChangeModule(module: any) {
    this.selectedModule = module
    this.moduleList.map((obj, index) => {
      if (obj.moduleId === module.moduleId) {
        obj["selected"] = true
      } else {
        obj["selected"] = false;
      }
    })
    this.propertyForm.controls['moduleId'].setValue(module.moduleId)
  }

  isvalidAttr(attributeId: string) {
    const ind = this.baiscAttrList.findIndex((prop) => prop.attributeId === attributeId);
    const index = this.baiscAttrList[ind].attributeProperties.findIndex((prop1: any) => prop1.selected && prop1.selected == true);
    if (index === -1) {
      this.valid = false;
      return false;

    } else {
      this.valid = true;
      return true;
    }

  }

  changeCompany(company: any) {
    this.selectedCompany = company
    this.companyList.map((obj, index) => {
      if (obj.companyId === company.companyId) {
        obj["selected"] = true
      } else {
        obj["selected"] = false
      }
    })
    this.propertyForm.controls['companyId'].setValue(company.companyId);

  }
  changeCategory(category: any) {
    this.selectedCategory = category
    this.categoryList.map((obj, index) => {
      if (obj.categoryId === category.categoryId) {
        obj["selected"] = true
        this.subCatList = obj.subCategory
      } else {
        obj["selected"] = false
      }
    })
    this.propertyForm.controls['categoryId'].setValue(category.categoryId);

  }

  get towers(): FormArray {
    return this.propertyForm.get('towers') as FormArray;
  }

  createTower(name = ''): FormGroup {
    return this.fb.group({
      // ✅ NAME: Required, length validation
      name: [name, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      isEditing: [false],

      // ✅ FLOORS: Required, positive integer - Initialize with null for number field
      floors: [null, [Validators.required, Validators.min(1), Validators.max(100), Validators.pattern(/^\d+$/)]],
      
      // ✅ TOTAL UNITS: Required, positive integer - Initialize with null for number field
      totalUnits: [null, [Validators.required, Validators.min(1), Validators.max(10000), Validators.pattern(/^\d+$/)]],
      
      // ✅ MIN AREA: Required, positive, reasonable minimum - Initialize with null for number field
      minArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ MAX AREA: Required, positive, must be > minArea - Initialize with null for number field
      maxArea: [null, [Validators.required, Validators.min(100), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      
      // ✅ PRICE PER SQFT: Required, positive - Initialize with null for number field
      pricePerSqft: [null, [Validators.required, Validators.min(50), Validators.max(100000), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],

      // ✅ FACING: Group of checkboxes (at least one should be selected)
      facing: this.fb.group({
        east: [false],
        west: [false],
        north: [false],
        south: [false]
      }),

      configurations: this.fb.array([
        this.createConfigRow()
      ])
    });
  }

  enableEditTower(tIndex: number) {
    this.towers.at(tIndex).patchValue({ isEditing: true });
  }

  saveTowerName(tIndex: number) {
    this.towers.at(tIndex).patchValue({ isEditing: false });
  }
  addTower() {
    const count = this.towers.length + 1;
    this.towers.push(this.createTower('Tower ' + count));
    // ✅ Add default configuration row to new tower
    const newTowerIndex = this.towers.length - 1;
    this.addConfigRowToTower(newTowerIndex);
  }

  removeTower(index: number) {
    this.towers.removeAt(index);
  }

  getConfigurations(tIndex: number): FormArray {
    return this.towers.at(tIndex).get('configurations') as FormArray;
  }

  addConfigRowToTower(tIndex: number) {
    this.getConfigurations(tIndex).push(this.createConfigRow());
    this.updateCachedTowerIndices(tIndex);
  }

  removeConfigRowFromTower(tIndex: number, i: number) {
    if (this.getConfigurations(tIndex).length > 1) {
      this.getConfigurations(tIndex).removeAt(i);
      this.updateCachedTowerIndices(tIndex);
    }
  }

  changeSubCategory(subCategory: any) {
    // ✅ Handle undefined/null subcategory
    if (!subCategory || !subCategory.subCategoryId) {
      console.warn('changeSubCategory called with invalid subcategory:', subCategory);
      return;
    }
    
    this.selectedSubCategory = subCategory;
    this.subCatList.map((obj, index) => {
      if (obj.subCategoryId === subCategory.subCategoryId) {
        obj["selected"] = true
      } else {
        obj["selected"] = false
      }
    })
    this.propertyForm.controls['subCategoryId'].setValue(subCategory.subCategoryId);
    
    // ✅ CLEAR ALL TOP-LEVEL FORM FIELD VALUES
    this.propertyForm.patchValue({
      projectName: '',
      projectDescription: '',
      totalUnits: null,
      minArea: null,
      maxArea: null,
      structure: '',
      facing: {
        east: false,
        west: false,
        north: false,
        south: false
      }
    });
    
    // Clear all form arrays
    this.towers.clear();
    this.phases.clear();
    this.configurations.clear();
    this.plotPhases.clear();
    this.venturePhases.clear();
    
    // Clear and reset all validators first
    this.resetAllValidators();
    
    const subCatName = subCategory.subCategoryName?.toLowerCase() || '';

    if (subCatName.includes('apartment')) {
      this.initializeApartmentConfig();
    } else if (subCatName.includes('independent')) {
      this.initializeIndependentConfig();
    } else if (subCatName.includes('villa')) {
      this.initializeVillaConfig();
    } else if (subCatName.includes('society') || subCatName.includes('other')) {
      this.initializePlotConfig();
    } else if (subCatName.includes('venture')) {
      this.initializeVentureConfig();
    }

    this.showIndependentConfig = subCategory.subCategoryName;
    this.attributePropertyIds = [];
    
    // ✅ CRITICAL: Recalculate form validity after all initialization
    this.propertyForm.updateValueAndValidity({ emitEvent: true });
  }

  /**
   * Reset all validators for top-level fields
   */
  private resetAllValidators(): void {
    ['totalUnits', 'minArea', 'maxArea', 'structure', 'facing'].forEach(fieldName => {
      const control = this.propertyForm.get(fieldName);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity({ emitEvent: true });
      }
    });
    
    // ✅ Clear validators from all empty FormArrays
    const emptyArrays = ['towers', 'phases', 'plotPhases', 'venturePhases'];
    emptyArrays.forEach(arrayName => {
      const array = this.propertyForm.get(arrayName) as FormArray;
      if (array) {
        array.clearValidators();
        array.updateValueAndValidity({ emitEvent: true });
      }
    });
  }

  /**
   * Change project status
   */
  changeProjectStatus(status: string): void {
    this.projectStatus = status;
  }

  /**
   * Debug helper: Log which form fields are invalid
   */
  debugFormValidation(): void {
    console.log('Form Valid:', this.propertyForm.valid);
    console.log('Form Status:', this.propertyForm.status);
    console.log('Form Errors:', this.propertyForm.errors);
    
    // Check all form controls
    Object.keys(this.propertyForm.controls).forEach(key => {
      const control = this.propertyForm.get(key);
      if (control && control.invalid) {
        console.log(`❌ ${key}:`, control.errors, 'Value:', control.value);
      } else {
        console.log(`✅ ${key}: Valid`);
      }
    });
  }

  /**
   * Initialize Apartment configuration (Towers + Units)
   */
  private initializeApartmentConfig(): void {
    console.log('===== initializeApartmentConfig() CALLED =====');
    
    // ✅ Clear facing validator - apartments don't use facing
    this.propertyForm.get('facing')?.clearValidators();
    this.propertyForm.get('facing')?.updateValueAndValidity({ emitEvent: true });
    
    // ✅ Get saved configuration data if it exists
    const savedProperty = this.lookupModel.getVendorProperty();
    const savedApartmentConfigs = savedProperty?.configuration && Array.isArray(savedProperty.configuration) 
      ? savedProperty.configuration 
      : null;
    
    // ✅ Check if we have saved towers to restore
    if (savedApartmentConfigs && savedApartmentConfigs.length > 0) {
      console.log('Restoring Apartment configuration with', savedApartmentConfigs.length, 'towers');
      
      // Restore each tower
      savedApartmentConfigs.forEach((tower: any, tIndex: number) => {
        console.log(`Adding tower ${tIndex}: ${tower.name}`);
        // ✅ Don't call addTower() which adds a default empty row - create tower directly
        const newTower = this.createTower(tower.name);
        this.towers.push(newTower);
        
        const towerControl = this.towers.at(tIndex) as FormGroup;
        
        // ✅ Convert saved facing array back to object format
        const facingObj = this.convertFacingArrayToObject(tower.facing);
        
        // ✅ Patch ALL values including nested facing group in one call
        towerControl.patchValue({
          name: tower.name || '',
          floors: tower.floors || null,
          totalUnits: tower.totalUnits || null,
          minArea: tower.minArea || null,
          maxArea: tower.maxArea || null,
          pricePerSqft: tower.pricePerSqft || null,
          facing: facingObj
        });
        console.log(`Restored tower ${tIndex} with facing:`, facingObj);
        
        // Restore configurations for this tower
        const configs = towerControl.get('configurations') as FormArray;
        
        // ✅ IMPORTANT: Clear the default empty row that createTower() adds
        while (configs.length > 0) {
          configs.removeAt(0);
        }
        
        if (tower.configurations && Array.isArray(tower.configurations)) {
          tower.configurations.forEach((config: any, cIndex: number) => {
            // Add a fresh config row
            const row = this.createConfigRow();
            row.patchValue({
              facing: config.facing || '',
              bhk: config.bhk || '',
              sqft: config.sqft || null,
              price: config.price || null,
              units: config.units || null,
              available: config.available || null
            });
            configs.push(row);
            console.log(`Restored config row ${cIndex} for tower ${tIndex}`);
          });
        }
        
        // ✅ Update cached indices for this tower (not just tower 0)
        this.updateCachedTowerIndices(tIndex);
      });
      console.log('Apartment restoration completed');
      
      // ✅ FORCE CHANGE DETECTION: Ensure template updates with the restored data
      setTimeout(() => {
        this.cdr.detectChanges();
        console.log('Apartment change detection triggered');
      }, 0);
    } else {
      // No saved data - add one fresh tower
      console.log('No saved Apartment configuration found, adding fresh empty tower');
      this.addTower();
    }
    
    console.log('===== initializeApartmentConfig() COMPLETED =====');
    console.log('Total towers:', this.towers.length);
  }

  /**
   * Initialize Independent House configuration (Direct configs + top-level fields)
   */
  private initializeIndependentConfig(): void {
    console.log('===== initializeIndependentConfig() CALLED =====');
    console.log('Configurations FormArray length BEFORE clearing:', this.configurations.length);
    
    // ✅ Get saved configuration data if it exists
    const savedProperty = this.lookupModel.getVendorProperty();
    const savedConfiguration = savedProperty?.configuration && Array.isArray(savedProperty.configuration) 
      ? savedProperty.configuration[0] 
      : null;
    
    // ✅ STEP 1: CLEAR the configurations FormArray completely (remove any leftover rows)
    while (this.configurations.length > 0) {
      this.configurations.removeAt(0);
    }
    
    console.log('Configurations FormArray length AFTER clearing:', this.configurations.length);
    
    // ✅ STEP 2: Check if we have saved data to restore
    if (savedConfiguration && this.showIndependentConfig === 'Independent House') {
      // ✅ RESTORE: Load previously saved configuration data
      console.log('Loading saved Independent House configuration:', savedConfiguration);
      
      // ✅ RESTORE TOP-LEVEL FIELDS
      if (savedConfiguration.totalUnits) {
        this.propertyForm.get('totalUnits')?.setValue(savedConfiguration.totalUnits);
      }
      if (savedConfiguration.minArea) {
        this.propertyForm.get('minArea')?.setValue(savedConfiguration.minArea);
      }
      if (savedConfiguration.maxArea) {
        this.propertyForm.get('maxArea')?.setValue(savedConfiguration.maxArea);
      }
      if (savedConfiguration.structure) {
        this.propertyForm.get('structure')?.setValue(savedConfiguration.structure);
      }
      
      // ✅ RESTORE FACING CHECKBOXES - Convert array back to object
      if (savedConfiguration.facing && Array.isArray(savedConfiguration.facing)) {
        const facingGroup = this.propertyForm.get('facing') as FormGroup;
        if (facingGroup) {
          // Reset all checkboxes first
          facingGroup.patchValue({
            east: false,
            west: false,
            north: false,
            south: false
          });
          
          // Then set the saved ones
          savedConfiguration.facing.forEach((facing: string) => {
            const facingKey = facing.toLowerCase();
            if (facingGroup.get(facingKey)) {
              facingGroup.get(facingKey)?.setValue(true);
            }
          });
        }
      }
      
      // ✅ RESTORE CONFIGURATION ROWS
      if (savedConfiguration.configurations && Array.isArray(savedConfiguration.configurations)) {
        console.log('Restoring configuration rows:', savedConfiguration.configurations);
        console.log('Number of saved config rows:', savedConfiguration.configurations.length);
        
        // Load each saved configuration row
        savedConfiguration.configurations.forEach((config: any, index: number) => {
          console.log(`Adding config row ${index}`);
          // Add a fresh row for each saved config
          this.addConfigRow();
          
          console.log(`Total configs after addConfigRow ${index}:`, this.configurations.length);
          
          // Populate it with saved data
          const rowControl = this.configurations.at(index) as FormGroup;
          rowControl.patchValue({
            facing: config.facing || '',
            bhk: config.bhk || '',
            sqft: config.sqft || null,
            price: config.price || null,
            units: config.units || null,
            available: config.available || null
          });
          
          // ✅ Mark as touched and update validity
          rowControl.updateValueAndValidity({ emitEvent: false });
          
          console.log(`Restored config row ${index}:`, rowControl.getRawValue());
        });
        
        // ✅ CRITICAL: Update cached indices after all rows are added
        console.log('Updating cached indices after restoration');
        this.updateCachedConfigIndices();
        console.log('Cached indices after update:', this.getCachedConfigIndices());
        console.log('Total configurations in FormArray:', this.configurations.length);
        console.log('FormArray controls:', this.configurations.controls.length);
        
        // ✅ FORCE CHANGE DETECTION: Ensure template updates with the restored data
        setTimeout(() => {
          this.cdr.detectChanges();
          console.log('Change detection triggered');
        }, 0);
      }
    } else {
      // ✅ STEP 3: No saved data - add one fresh empty row for user to start filling in
      console.log('No saved configuration found, adding fresh empty row');
      this.addConfigRow();
    }
    
    // ✅ Set validator for facing (at least one direction must be selected)
    this.propertyForm.get('facing')?.setValidators([atLeastOneCheckboxSelectedValidator()]);
    
    // Set validators for top-level fields
    this.propertyForm.get('totalUnits')?.setValidators([
      Validators.required,
      Validators.min(1),
      // ✅ ADD VALIDATOR: Total units must match sum of configuration units
      totalUnitsMatchValidator(() => this.configurations)
    ]);
    // ✅ REMOVED minArea/maxArea validators - they're optional for Independent House
    this.propertyForm.get('minArea')?.clearValidators();
    this.propertyForm.get('maxArea')?.clearValidators();
    
    this.propertyForm.get('structure')?.setValidators([
      Validators.required
    ]);
    
    // Update validity - use emitEvent: true to notify form of changes
    ['facing', 'totalUnits', 'minArea', 'maxArea', 'structure'].forEach(fieldName => {
      this.propertyForm.get(fieldName)?.updateValueAndValidity({ emitEvent: true });
    });

    // ✅ SET UP LISTENER: Update totalUnits validation whenever configuration units change
    this.configurations.valueChanges.subscribe(() => {
      this.propertyForm.get('totalUnits')?.updateValueAndValidity({ emitEvent: true });
      // ✅ CRITICAL: Also update parent form validity so the Next button can respond
      this.propertyForm.updateValueAndValidity({ emitEvent: true });
    });

    // ✅ DEFENSIVE: Ensure there's always at least one row
    if (this.configurations.length === 0) {
      console.log('Adding fallback empty row');
      this.addConfigRow();
    }
    
    console.log('===== initializeIndependentConfig() COMPLETED =====');
    console.log('Final FormArray length:', this.configurations.length);
    console.log('Final cached indices:', this.getCachedConfigIndices());
  }

  /**
   * Initialize Villa configuration (Phase-based)
   */
  private initializeVillaConfig(): void {
    console.log('===== initializeVillaConfig() CALLED =====');
    
    // ✅ Clear facing validator - villas don't use facing
    this.propertyForm.get('facing')?.clearValidators();
    this.propertyForm.get('facing')?.updateValueAndValidity({ emitEvent: true });
    
    // ✅ Get saved configuration data if it exists
    const savedProperty = this.lookupModel.getVendorProperty();
    const savedVillaConfigs = savedProperty?.configuration && Array.isArray(savedProperty.configuration) 
      ? savedProperty.configuration 
      : null;
    
    // ✅ Check if we have saved phases to restore
    if (savedVillaConfigs && savedVillaConfigs.length > 0) {
      console.log('Restoring Villa configuration with', savedVillaConfigs.length, 'phases');
      
      // Restore each phase
      savedVillaConfigs.forEach((phase: any, pIndex: number) => {
        console.log(`Adding phase ${pIndex}: ${phase.name}`);
        // ✅ Don't call addPhase() which adds a default empty row - create phase directly
        const newPhase = this.createPhase(phase.name);
        this.phases.push(newPhase);
        
        const phaseControl = this.phases.at(pIndex) as FormGroup;
        
        // ✅ Convert saved arrays back to boolean objects
        const structureObj = this.convertStructureArrayToObject(phase.structure);
        const facingObj = this.convertFacingArrayToObject(phase.facing);
        
        // ✅ Patch ALL values including nested structure and facing groups in one call
        phaseControl.patchValue({
          name: phase.name || '',
          totalUnits: phase.totalUnits || null,
          minArea: phase.minArea || null,
          maxArea: phase.maxArea || null,
          villaArea: phase.villaArea || null,
          structure: structureObj,
          facing: facingObj
        });
        console.log(`Restored phase ${pIndex} with structure:`, structureObj, 'facing:', facingObj);
        
        // Restore configurations for this phase
        const configs = phaseControl.get('configurations') as FormArray;
        if (phase.configurations && Array.isArray(phase.configurations)) {
          phase.configurations.forEach((config: any, cIndex: number) => {
            const row = this.createConfigRow();
            row.patchValue({
              facing: config.facing || '',
              bhk: config.bhk || '',
              sqft: config.sqft || null,
              price: config.price || null,
              units: config.units || null,
              available: config.available || null
            });
            configs.push(row);
            console.log(`Restored config row ${cIndex} for phase ${pIndex}`);
          });
        }
        
        // ✅ Update cached indices for this phase (not just phase 0)
        this.updateCachedPhaseIndices(pIndex);
      });
      console.log('Villa restoration completed');
      
      // ✅ FORCE CHANGE DETECTION: Ensure template updates with the restored data
      setTimeout(() => {
        this.cdr.detectChanges();
        console.log('Villa change detection triggered');
      }, 0);
    } else {
      // No saved data - add one fresh phase
      console.log('No saved Villa configuration found, adding fresh empty phase');
      this.addPhase();
      // ✅ Add one empty row for user to start entering data
      this.addConfigRowToPhase(0);
    }
    
    // ✅ SET UP VALIDATORS AND LISTENERS for each phase
    this.phases.controls.forEach((phaseControl, pIndex) => {
      this.setupVillaPhaseValidators(pIndex);
    });
    
    console.log('===== initializeVillaConfig() COMPLETED =====');
    console.log('Total phases:', this.phases.length);
  }

  /**
   * Helper method: Set up validators and listeners for a specific Villa phase
   * Called during initialization AND when a new phase is added
   */
  private setupVillaPhaseValidators(pIndex: number): void {
    const phaseControl = this.phases.at(pIndex);
    if (!phaseControl) return;
    
    const phaseFormGroup = phaseControl as FormGroup;
    
    // Set totalUnits validator - must match sum of configuration units
    phaseFormGroup.get('totalUnits')?.setValidators([
      Validators.required,
      Validators.min(1),
      totalUnitsMatchValidator(() => phaseFormGroup.get('configurations') as FormArray)
    ]);
    phaseFormGroup.get('totalUnits')?.updateValueAndValidity({ emitEvent: false });
    
    // Set up listener: Update totalUnits validation whenever configuration units change
    const configs = phaseFormGroup.get('configurations') as FormArray;
    configs.valueChanges.subscribe(() => {
      phaseFormGroup.get('totalUnits')?.updateValueAndValidity({ emitEvent: true });
      phaseFormGroup.updateValueAndValidity({ emitEvent: true });
    });
    
    // ✅ Update overall form validity
    this.propertyForm.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Initialize Plot configuration (Plot Phases)
   */
  private initializePlotConfig(): void {
    console.log('===== initializePlotConfig() CALLED =====');
    
    // ✅ Clear facing validator - plots don't use facing
    this.propertyForm.get('facing')?.clearValidators();
    this.propertyForm.get('facing')?.updateValueAndValidity({ emitEvent: true });
    
    // ✅ Get saved configuration data if it exists
    const savedProperty = this.lookupModel.getVendorProperty();
    const savedPlotConfigs = savedProperty?.configuration && Array.isArray(savedProperty.configuration) 
      ? savedProperty.configuration 
      : null;
    
    // ✅ Check if we have saved plot phases to restore
    if (savedPlotConfigs && savedPlotConfigs.length > 0) {
      console.log('Restoring Plot configuration with', savedPlotConfigs.length, 'plot phases');
      
      // Restore each plot phase
      savedPlotConfigs.forEach((plotPhase: any, pIndex: number) => {
        console.log(`Adding plot phase ${pIndex}: ${plotPhase.name}`);
        // ✅ Don't call addPlotPhase() which adds a default empty row - create phase directly
        const newPlotPhase = this.createPlotPhase(plotPhase.name);
        this.plotPhases.push(newPlotPhase);
        
        const plotPhaseControl = this.plotPhases.at(pIndex) as FormGroup;
        
        // ✅ Convert saved arrays back to object format
        const structureObj = this.convertStructureArrayToObject(plotPhase.structure);
        const facingObj = this.convertFacingArrayToObject(plotPhase.facing);
        
        // ✅ Patch ALL values including nested structure and facing groups in one call
        plotPhaseControl.patchValue({
          name: plotPhase.name || '',
          totalUnits: plotPhase.totalUnits || null,
          totalArea: plotPhase.totalArea || null,
          plotSizeUnit: plotPhase.plotSizeUnit || '',
          minSize: plotPhase.minArea || null,
          maxSize: plotPhase.maxArea || null,
          length: plotPhase.length || null,
          width: plotPhase.width || null
        });
        
        // ✅ Patch nested FormGroups separately to ensure they update properly
        const structureFormGroup = plotPhaseControl.get('structure') as FormGroup;
        if (structureFormGroup) {
          structureFormGroup.patchValue(structureObj);
          structureFormGroup.updateValueAndValidity({ emitEvent: false });
        }
        
        const facingFormGroup = plotPhaseControl.get('facing') as FormGroup;
        if (facingFormGroup) {
          facingFormGroup.patchValue(facingObj);
          facingFormGroup.updateValueAndValidity({ emitEvent: false });
        }
        
        console.log(`Restored plot phase ${pIndex} with all fields:`, {
          name: plotPhase.name,
          totalUnits: plotPhase.totalUnits,
          totalArea: plotPhase.totalArea,
          plotSizeUnit: plotPhase.plotSizeUnit,
          minSize: plotPhase.minArea,
          maxSize: plotPhase.maxArea,
          length: plotPhase.length,
          width: plotPhase.width,
          structure: structureObj,
          facing: facingObj
        });
        
        // Restore configurations for this plot phase
        const configs = plotPhaseControl.get('configurations') as FormArray;
        if (plotPhase.configurations && Array.isArray(plotPhase.configurations)) {
          plotPhase.configurations.forEach((config: any, cIndex: number) => {
            // ✅ Use createPlotRow() for plot-specific fields, NOT createConfigRow()
            const row = this.createPlotRow();
            row.patchValue({
              facing: config.facing || '',
              plotArea: config.plotArea || null,
              sqftPrice: config.sqftPrice || null,
              unitPrice: config.unitPrice || null,
              units: config.units || null,
              available: config.available || null
            });
            configs.push(row);
            console.log(`Restored plot config row ${cIndex} for plot phase ${pIndex}:`, config);
          });
        }
        
        // ✅ Update cached indices for this plot phase (not just plot phase 0)
        this.updateCachedPlotIndices(pIndex);
      });
      console.log('Plot restoration completed');
      
      // ✅ UPDATE ALL FORM VALIDITY: After restoring all phases, update the entire form
      this.propertyForm.updateValueAndValidity({ emitEvent: false });
      
      // ✅ FORCE CHANGE DETECTION: Ensure template updates with the restored data
      setTimeout(() => {
        this.cdr.detectChanges();
        console.log('Plot change detection triggered');
        console.log('Form status after restoration:', this.propertyForm.status);
      }, 0);
    } else {
      // No saved data - add one fresh plot phase
      console.log('No saved Plot configuration found, adding fresh empty plot phase');
      this.addPlotPhase();
      // ✅ Add one empty row for user to start entering data
      this.addConfigRowToPlot(0);
    }
    
    // ✅ SET UP VALIDATORS AND LISTENERS for each plot phase
    this.plotPhases.controls.forEach((phaseControl, pIndex) => {
      this.setupPlotPhaseValidators(pIndex);
    });
    
    console.log('===== initializePlotConfig() COMPLETED =====');
    console.log('Total plot phases:', this.plotPhases.length);
  }

  /**
   * Helper method: Set up validators and listeners for a specific Plot phase
   * Called during initialization AND when a new phase is added
   */
  private setupPlotPhaseValidators(pIndex: number): void {
    const phaseControl = this.plotPhases.at(pIndex);
    if (!phaseControl) return;
    
    const phaseFormGroup = phaseControl as FormGroup;
    
    // Set totalUnits validator - must match sum of configuration units
    phaseFormGroup.get('totalUnits')?.setValidators([
      Validators.required,
      Validators.min(1),
      totalUnitsMatchValidator(() => phaseFormGroup.get('configurations') as FormArray)
    ]);
    phaseFormGroup.get('totalUnits')?.updateValueAndValidity({ emitEvent: false });
    
    // Set up listener: Update totalUnits validation whenever configuration units change
    const configs = phaseFormGroup.get('configurations') as FormArray;
    configs.valueChanges.subscribe(() => {
      phaseFormGroup.get('totalUnits')?.updateValueAndValidity({ emitEvent: true });
      phaseFormGroup.updateValueAndValidity({ emitEvent: true });
    });
    
    // ✅ Update overall form validity
    this.propertyForm.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Initialize Venture configuration (Venture Phases)
   */
  private initializeVentureConfig(): void {
    console.log('===== initializeVentureConfig() CALLED =====');
    
    // ✅ Clear facing validator - ventures don't use facing
    this.propertyForm.get('facing')?.clearValidators();
    this.propertyForm.get('facing')?.updateValueAndValidity({ emitEvent: true });
    
    // ✅ Get saved configuration data if it exists
    const savedProperty = this.lookupModel.getVendorProperty();
    const savedVentureConfigs = savedProperty?.configuration && Array.isArray(savedProperty.configuration) 
      ? savedProperty.configuration 
      : null;
    
    // ✅ Check if we have saved venture phases to restore
    if (savedVentureConfigs && savedVentureConfigs.length > 0) {
      console.log('Restoring Venture configuration with', savedVentureConfigs.length, 'venture phases');
      
      // Restore each venture phase
      savedVentureConfigs.forEach((venturePhase: any, vIndex: number) => {
        console.log(`Adding venture phase ${vIndex}: ${venturePhase.name}`);
        // ✅ Don't call addVenturePhase() which adds a default empty row - create phase directly
        const newVenturePhase = this.createVenturePhase(venturePhase.name);
        this.venturePhases.push(newVenturePhase);
        
        const venturePhaseControl = this.venturePhases.at(vIndex) as FormGroup;
        
        // ✅ Convert saved arrays back to object format
        const structureObj = this.convertStructureArrayToObject(venturePhase.structure);
        const facingObj = this.convertFacingArrayToObject(venturePhase.facing);
        
        // ✅ Patch ALL values including nested structure and facing groups in one call
        venturePhaseControl.patchValue({
          name: venturePhase.name || '',
          totalUnits: venturePhase.totalUnits || null,
          plotSizeUnit: venturePhase.plotSizeUnit || '',
          length: venturePhase.length || null,
          width: venturePhase.width || null,
          minArea: venturePhase.minArea || null,
          maxArea: venturePhase.maxArea || null,
          totalArea: (venturePhase.totalArea || '').replace(' Acre', '').replace(' Acres', '') || null
        });
        
        // ✅ Patch nested FormGroups separately to ensure they update properly
        const structureFormGroup = venturePhaseControl.get('structure') as FormGroup;
        if (structureFormGroup) {
          structureFormGroup.patchValue(structureObj);
          structureFormGroup.updateValueAndValidity({ emitEvent: false });
        }
        
        const facingFormGroup = venturePhaseControl.get('facing') as FormGroup;
        if (facingFormGroup) {
          facingFormGroup.patchValue(facingObj);
          facingFormGroup.updateValueAndValidity({ emitEvent: false });
        }
        
        console.log(`Restored venture phase ${vIndex} with all fields:`, {
          name: venturePhase.name,
          totalUnits: venturePhase.totalUnits,
          plotSizeUnit: venturePhase.plotSizeUnit,
          length: venturePhase.length,
          width: venturePhase.width,
          minArea: venturePhase.minArea,
          maxArea: venturePhase.maxArea,
          totalArea: venturePhase.totalArea,
          structure: structureObj,
          facing: facingObj
        });
        
        // Restore configurations for this venture phase
        const configs = venturePhaseControl.get('configurations') as FormArray;
        if (venturePhase.configurations && Array.isArray(venturePhase.configurations)) {
          venturePhase.configurations.forEach((config: any, cIndex: number) => {
            // ✅ Use createVentureRow() for venture-specific fields, NOT createConfigRow()
            const row = this.createVentureRow();
            row.patchValue({
              facing: config.facing || '',
              villaArea: config.villaArea || null,
              bhk: config.bhk || '',
              sqFeet: config.sqFeet || null,
              sqFeetPrice: config.sqFeetPrice || null,
              unitPrice: config.unitPrice || null,
              units: config.units || null
            });
            configs.push(row);
            console.log(`Restored venture config row ${cIndex} for venture phase ${vIndex}:`, config);
          });
        }
        
        // ✅ Update cached indices for this venture phase (not just venture phase 0)
        this.updateCachedVentureIndices(vIndex);
      });
      console.log('Venture restoration completed');
      
      // ✅ UPDATE ALL FORM VALIDITY: After restoring all phases, update the entire form
      this.propertyForm.updateValueAndValidity({ emitEvent: false });
      
      // ✅ FORCE CHANGE DETECTION: Ensure template updates with the restored data
      setTimeout(() => {
        this.cdr.detectChanges();
        console.log('Venture change detection triggered');
        console.log('Form status after restoration:', this.propertyForm.status);
      }, 0);
    } else {
      // No saved data - add one fresh venture phase
      console.log('No saved Venture configuration found, adding fresh empty venture phase');
      this.addVenturePhase();
      // ✅ Add one empty row for user to start entering data
      this.addConfigRowToVenture(0);
    }
    
    // ✅ SET UP VALIDATORS AND LISTENERS for each venture phase
    this.venturePhases.controls.forEach((phaseControl, vIndex) => {
      this.setupVenturePhaseValidators(vIndex);
    });
    
    console.log('===== initializeVentureConfig() COMPLETED =====');
    console.log('Total venture phases:', this.venturePhases.length);
  }

  /**
   * Helper method: Set up validators and listeners for a specific venture phase
   * Called during initialization AND when a new phase is added
   */
  private setupVenturePhaseValidators(vIndex: number): void {
    const phaseControl = this.venturePhases.at(vIndex);
    if (!phaseControl) return;
    
    const phaseFormGroup = phaseControl as FormGroup;
    
    // Set totalUnits validator - must match sum of configuration units
    phaseFormGroup.get('totalUnits')?.setValidators([
      Validators.required,
      Validators.min(1),
      totalUnitsMatchValidator(() => phaseFormGroup.get('configurations') as FormArray)
    ]);
    phaseFormGroup.get('totalUnits')?.updateValueAndValidity({ emitEvent: false });
    
    // Set up listener: Update totalUnits validation whenever configuration units change
    const configs = phaseFormGroup.get('configurations') as FormArray;
    configs.valueChanges.subscribe(() => {
      phaseFormGroup.get('totalUnits')?.updateValueAndValidity({ emitEvent: true });
      phaseFormGroup.updateValueAndValidity({ emitEvent: true });
    });
    
    // ✅ Update overall form validity
    this.propertyForm.updateValueAndValidity({ emitEvent: false });
  }

  addConfigRow() {
    this.configurations.push(this.createConfigRow());
    console.log('=== ADD CONFIG ROW ===');
    console.log('Total configurations after adding:', this.configurations.length);
    console.log('All configuration values:', this.configurations.getRawValue());
    this.updateCachedConfigIndices();
    // ✅ Update form validity after adding a row
    this.propertyForm.updateValueAndValidity({ emitEvent: true });
  }

  removeConfigRow(index: number) {
    if (this.configurations.length > 1) {
      this.configurations.removeAt(index);
      this.updateCachedConfigIndices();
      // ✅ Update form validity after removing a row
      this.propertyForm.updateValueAndValidity({ emitEvent: true });
    }
  }

  disableForm(): void {
    this.propertyForm.disable({ emitEvent: false });
  }

  findCountryIso(key: string): string | undefined {
    const lowerKey = key.toLowerCase();
    for (const [k, v] of Object.entries(CountryISO)) {
      if (v === lowerKey) {
        return v;
      }
    }
    return undefined;
  }
  getModules(): void {
    this.rsbService.getActiveModules(this.moduleId?.value, this.selectedCountry.codeId).subscribe((rsp) => {
      if (rsp.statusCode === 0 && rsp.contents) {

        this.moduleList = rsp.contents.filter((module: any) => module.moduleName.toLowerCase() === 'projects');
        this.selectedModule = this.moduleList.find(item => item.moduleId === this.moduleId?.value || item.moduleId === this.selectedProperty?.moduleId) || this.moduleList[0];
        this.moduleId?.setValue(this.selectedModule.moduleId);
        this.getCategories(this.moduleId?.value);
        this.getActiveMeasurements();
        this.onChangeModule(this.selectedModule);
        setTimeout(() => {

          this.changeCategory(this.selectedCategory);
          this.changeSubCategory(this.selectedSubCategory);
          // ✅ REMOVED: loadSavedConfiguration() was redundant and conflicting with 
          // initialization methods that already handle restoration
        }, 1000);
        // this.changeModuleFields(true);
      }
    });
  }

  getActiveMeasurements(): void {
    this.rsbService.getActiveMeasurements().subscribe((rsp) => {
      if (rsp?.statusCode === 0 && rsp?.contents) {
        this.measurementList = rsp.contents;
      }
    });
  }
  getCategories(moduleId: string): void {
    this.rsbService.getActiveCategories(moduleId).subscribe((rsp) => {
      if (rsp?.statusCode === 0 && rsp?.contents) {
        this.categoryList = rsp.contents;

        if (this.selectedProperty?.categoryId) {
          const category: any = this.categoryList.find((cat) => cat?.categoryId === this.selectedProperty?.categoryId);
          if (category) {
            this.selectedCategory = category;
            this.subCatList = category?.subCategory || [];
            this.selectedSubCategory = this.subCatList.find((subcat) => subcat?.subCategoryId === this.selectedProperty?.subCategoryId);
            // ✅ Set showIndependentConfig immediately when restoring saved property
            if (this.selectedSubCategory) {
              this.showIndependentConfig = this.selectedSubCategory?.subCategoryName;
            }
            this.selectedMeasurement = this.selectedSubCategory;
            this.categoryId?.setValue(this.selectedProperty?.categoryId);
            this.subCategoryId?.setValue(this.selectedProperty?.subCategoryId);
            if (this.checkReadOnly()) {
              this.disableForm();
            }
          }
        } else {
          this.categoryId?.reset();
          this.subCategoryId?.reset();
          this.subCategoryId?.setValue('');
          this.propertyForm?.removeControl('basicAttributes');
          this.propertyForm?.addControl('basicAttributes', new FormArray([]));
          this.firstLoad = false;
          this.selectedCategory = this.categoryList[0];
          this.subCatList = this.selectedCategory?.subCategory || [];
          this.selectedSubCategory = this.subCatList[0];
          // ✅ Set showIndependentConfig for new property
          this.showIndependentConfig = this.selectedSubCategory?.subCategoryName || '';
          this.selectedMeasurement = this.selectedSubCategory;
          this.categoryId?.setValue(this.selectedCategory?.categoryId);
          this.subCategoryId?.setValue(this.selectedSubCategory?.subCategoryId);
        }
      }
    });
  }

  getCountryList(): void {
    this.countryList = this.lookupModel?.getCountryList() || [];
  }

  // getMeasurementList(): void {
  //   this.rsbService.getActiveMeasurements().subscribe((rsp) => {
  //     if (rsp.statusCode === 0 && rsp.contents) {
  //       this.measurementList = rsp.contents;
  //       if (!this.measurement.value) {
  //         this.measurement.setValue(this.measurementList[0].measurementId);
  //       }
  //     }
  //   });
  //   // this.measurement.disable();
  // }

  prepareLocationText(propertyInfo: any): string {
    let locationText = '';
    if (propertyInfo.addressLine1) {
      locationText += propertyInfo.addressLine1 + ',';
    }

    if (propertyInfo.landmark) {
      locationText += propertyInfo.landmark + ',';
      const index = this.titleProperties.findIndex(item => item.name === 'LOCATION');
      this.titleProperties[index].value = propertyInfo.landmark + ', ';
    }

    if (propertyInfo.locality) {
      locationText += propertyInfo.locality + ',';
      const index = this.titleProperties.findIndex(item => item.name === 'LOCATION');
      this.titleProperties[index].value = this.titleProperties[index].value + propertyInfo.locality + ', ';
    }

    if (propertyInfo.city) {
      locationText += propertyInfo.city + ',';
      const index = this.titleProperties.findIndex(item => item.name === 'LOCATION');
      this.titleProperties[index].value = this.titleProperties[index].value + propertyInfo.city;
    }

    if (propertyInfo.countryName) {
      locationText += propertyInfo.countryName + ',';
    }

    if (propertyInfo.pincode) {
      locationText += propertyInfo.pincode;
    }
    return locationText;
  }

  preTitle(prop?: any, index?: number): void {
    if (index !== undefined) {
      this.availableBhk[index].selected = this.availableBhk[index].selected ? false : true
      this.availableBhk[index].buttonStatus = this.availableBhk[index].buttonStatus ? 'active' : 'deactive';
    }
  }

  validAttr() {
    const allBhk = this.availableBhk.filter((obj) => {
      return obj.selected === true
    })

    const val = allBhk.length ? true : false
    return val;
  }
  formChanges(): void {
    this.propertyForm.valueChanges.subscribe((val) => {
      // console.log('Property Form Value changes :: ', val);
    });

    this.moduleId?.valueChanges.subscribe((val) => {
      if (val) {
        this.selectedModule = this.moduleList.find(item => item.moduleId === val);
        this.getCategories(val);
        // this.changeModuleFields(val);

      }
    });

    this.categoryId?.valueChanges.subscribe((val) => {
      if (val) {
        // this.loadAttributes(val);
        let category: any = this.categoryList.filter((cat) => cat.categoryId === val);
        this.selectedCategory = category[0];
        this.subCatList = category[0].subCategory;
        this.selectedSubCategory = null;
        if (this.selectedProperty && this.selectedProperty.projectId && this.firstLoad && val === this.selectedProperty.categoryId) {
          this.firstLoad = false;
        }
      }
    });

    this.subCategoryId?.valueChanges.subscribe((val) => {
      if (val) {
        const subCat = this.subCatList.filter((subcat) => subcat.subCategoryId === val)[0];
        if (subCat) {
          this.selectedSubCategory = subCat;
          this.selectedMeasurement = this.selectedSubCategory;
          // ✅ Also set showIndependentConfig when subcategory changes
          this.showIndependentConfig = subCat.subCategoryName;
        }
        // this.measurement.setValue(this.selectedMeasurement.measurementId);
      }
    });


  }






  changeLocation(): void {
    this.showNextPage.emit(1);
  }

  // changeMeasurment(): void {
  //   this.measurement.enable();
  // }

  // currencyChange(val: string): void {
  //   this.priceCurrency.setValue(val);
  //   this.depositCurrency.setValue(val);
  //   this.priceCurrency.disable();
  //   this.depositCurrency.disable();
  // }

  // changeCurrency(depositFlag?: boolean): void {
  //   if (depositFlag) {
  //     this.depositCurrency.enable();
  //   } else {
  //     this.priceCurrency.enable();
  //   }
  // }

  uploadAws(event: any): void {
    const mdlRef = this.modalService.open(ImageCropperComponent, { windowClass: 'signInForm' });
    mdlRef.componentInstance.imageChangedEvent = event;
    mdlRef.result.then((result) => {
      if (result) {
        const modalRef = this.modalService.open(UploadProgressComponent, { windowClass: 'signInForm' });
        modalRef.componentInstance.file = result;
        modalRef.result.then((rsp) => {
          if (rsp) {
            this.propertyPreviewImage = rsp;
          }
        });
      }
    });
  }

  SUBCATEGORY_CONFIG: Record<
    any,
    {
      validate: (form: FormGroup) => boolean;
      map: (form: any) => any;
    }
  > = {
      Apartment: {
        validate: (form: FormGroup) => this.validateApartment(form),
        map: (form: any) => this.mapApartment(form)
      },
      'Independent House': {
        validate: (form: FormGroup) => this.validateIndependent(form),
        map: (form: any) => this.mapIndependent(form)
      },
      Villa: {
        validate: (form: FormGroup) => this.validateVilla(form),
        map: (form: any) => this.mapVilla(form)
      },
      'Society Plot': {
        validate: (form: FormGroup) => this.validatePlot(form),
        map: (form: any) => this.mapPlot(form)
      },
      'Other Plot': {
        validate: (form: FormGroup) => this.validatePlot(form),
        map: (form: any) => this.mapPlot(form)
      },
      'Venture Plot': {
        validate: (form: FormGroup) => this.validateVenture(form),
        map: (form: any) => this.mapVenture(form)
      }
    };



  validateApartment(form: FormGroup): boolean {
    const towers = form.get('towers') as FormArray;

    // Must have at least one tower
    if (!towers || towers.length === 0) {
      console.warn('Apartment validation failed: No towers defined');
      return false;
    }

    return towers.controls.every((tower, tIndex) => {
      const towerForm = tower as FormGroup;
      
      // ✅ Validate tower-level fields
      const towerName = towerForm.get('name')?.value;
      const floors = towerForm.get('floors')?.value;
      const totalUnits = towerForm.get('totalUnits')?.value;
      const minArea = towerForm.get('minArea')?.value;
      const maxArea = towerForm.get('maxArea')?.value;
      
      if (!towerName || towerName.trim() === '') {
        console.warn(`Apartment validation failed: Tower ${tIndex} - name is required`);
        return false;
      }
      
      if (!floors || Number(floors) < 1) {
        console.warn(`Apartment validation failed: Tower ${tIndex} - floors must be >= 1`);
        return false;
      }
      
      if (!totalUnits || Number(totalUnits) < 1) {
        console.warn(`Apartment validation failed: Tower ${tIndex} - totalUnits must be >= 1`);
        return false;
      }
      
      if (minArea === null || minArea === undefined || Number(minArea) < 0) {
        console.warn(`Apartment validation failed: Tower ${tIndex} - minArea must be >= 0`);
        return false;
      }
      
      if (maxArea === null || maxArea === undefined || Number(maxArea) < 0) {
        console.warn(`Apartment validation failed: Tower ${tIndex} - maxArea must be >= 0`);
        return false;
      }
      
      if (minArea && maxArea && Number(minArea) > Number(maxArea)) {
        console.warn(`Apartment validation failed: Tower ${tIndex} - minArea > maxArea`);
        return false;
      }

      const configs = towerForm.get('configurations') as FormArray;

      // Each tower must have configurations
      if (!configs || configs.length === 0) {
        console.warn(`Apartment validation failed: Tower ${tIndex} has no configurations`);
        return false;
      }

      // Filter out empty rows (rows where bhk is empty) - these are newly added empty rows
      const filledConfigs = configs.controls.filter(config => {
        const bhk = config.get('bhk')?.value;
        return bhk && bhk !== '';
      });

      // Must have at least one filled configuration per tower
      if (filledConfigs.length === 0) {
        console.warn(`Apartment validation failed: Tower ${tIndex} has no completed configurations`);
        return false;
      }

      return filledConfigs.every((config, cIndex) => {
        const bhk = config.get('bhk')?.value;
        const sqft = config.get('sqft')?.value;
        const price = config.get('price')?.value;
        const units = config.get('units')?.value;
        const available = config.get('available')?.value;

        if (!bhk || bhk === '') {
          console.warn(`Apartment validation failed: Tower ${tIndex}, Config ${cIndex} - BHK is required`);
          return false;
        }
        
        if (!sqft || Number(sqft) <= 0) {
          console.warn(`Apartment validation failed: Tower ${tIndex}, Config ${cIndex} - sqft must be > 0`);
          return false;
        }
        
        if (!price || Number(price) <= 0) {
          console.warn(`Apartment validation failed: Tower ${tIndex}, Config ${cIndex} - price must be > 0`);
          return false;
        }
        
        if (!units || Number(units) <= 0) {
          console.warn(`Apartment validation failed: Tower ${tIndex}, Config ${cIndex} - units must be > 0`);
          return false;
        }
        
        if (available === null || available === undefined || Number(available) < 0) {
          console.warn(`Apartment validation failed: Tower ${tIndex}, Config ${cIndex} - available must be >= 0`);
          return false;
        }

        return true;
      });
    });
  }

  mapApartment(form: any) {
    const towers = form.towers || [];
    
    return towers.map((t: any) => ({
      name: t.name || '',
      floors: t.floors || '',
      totalUnits: t.totalUnits || '',
      minArea: t.minArea || '',
      maxArea: t.maxArea || '',
      pricePerSqFt: t.pricePerSqFt || '',
      facing: this.mapFacing(t.facing),
      // ✅ Filter out empty rows where bhk is empty
      configurations: (t.configurations || [])
        .filter((c: any) => c.bhk && c.bhk !== '')
        .map((c: any) => ({
          facing: c.facing || '',
          bhk: c.bhk,
          sqft: c.sqft,
          price: c.price,
          units: c.units,
          available: c.available || 0
        }))
    }));
  }

  validateIndependent(form: FormGroup): boolean {
    let isValid = true;

    // ✅ Top-level fields validation
    const totalUnits = form.get('totalUnits')?.value;
    const minArea = form.get('minArea')?.value;
    const maxArea = form.get('maxArea')?.value;

    if (!totalUnits || totalUnits < 1) {
      console.warn('Independent validation failed: totalUnits must be >= 1');
      isValid = false;
    }

    if (minArea === null || minArea === undefined || minArea < 0) {
      console.warn('Independent validation failed: minArea must be >= 0');
      isValid = false;
    }

    if (maxArea === null || maxArea === undefined || maxArea < 0) {
      console.warn('Independent validation failed: maxArea must be >= 0');
      isValid = false;
    }

    // ✅ Range validation: minArea <= maxArea
    if (isValid && minArea && maxArea && Number(minArea) > Number(maxArea)) {
      console.warn('Independent validation failed: minArea > maxArea');
      isValid = false;
    }
    
    // ✅ Validate structure - must have a value selected
    const structureValue = form.get('structure')?.value;
    if (!structureValue || structureValue === '') {
      console.warn('Independent validation failed: structure type must be selected');
      isValid = false;
    }
    
    // ✅ Validate facing checkbox - at least one must be selected
    const facing = form.get('facing') as FormGroup;
    const hasFacing = facing?.get('east')?.value || facing?.get('west')?.value || facing?.get('north')?.value || facing?.get('south')?.value;
    if (!hasFacing) {
      console.warn('Independent validation failed: at least one facing must be selected');
      isValid = false;
    }

    // ✅ Configuration table validation
    const configs = form.get('configurations') as FormArray;

    if (!configs || configs.length === 0) {
      console.warn('Independent validation failed: No configurations defined');
      return false;
    }

    // Filter out empty rows (rows where bhk is empty) - these are newly added empty rows
    const filledConfigs = configs.controls.filter(c => {
      const bhk = c.get('bhk')?.value;
      return bhk && bhk !== '';
    });

    // Must have at least one filled configuration
    if (filledConfigs.length === 0) {
      console.warn('Independent validation failed: No completed configurations');
      return false;
    }

    filledConfigs.forEach((c, idx) => {
      const facing = c.get('facing')?.value;
      const bhk = c.get('bhk')?.value;
      const sqft = c.get('sqft')?.value;
      const price = c.get('price')?.value;
      const units = c.get('units')?.value;
      const available = c.get('available')?.value;

      if (!facing || facing === '') {
        console.warn(`Independent validation failed: Config ${idx} - facing is required`);
        isValid = false;
      }

      if (!bhk) {
        console.warn(`Independent validation failed: Config ${idx} - BHK missing`);
        isValid = false;
      }

      if (!sqft || sqft <= 0) {
        console.warn(`Independent validation failed: Config ${idx} - sqft must be > 0`);
        isValid = false;
      }

      if (!price || price <= 0) {
        console.warn(`Independent validation failed: Config ${idx} - price must be > 0`);
        isValid = false;
      }

      if (!units || units <= 0) {
        console.warn(`Independent validation failed: Config ${idx} - units must be > 0`);
        isValid = false;
      }
      
      if (available === null || available === undefined || available < 0) {
        console.warn(`Independent validation failed: Config ${idx} - available must be >= 0`);
        isValid = false;
      }
    });

    return isValid;
  }
  mapIndependent(form: any) {
    console.log("form is", form)
    console.log("Total configs in form:", form.configurations?.length || 0);
    console.log("All configurations from form:", form.configurations);
    
    // ✅ Filter out empty rows (rows where bhk is empty)
    const filledConfigs = (form.configurations || []).filter((c: any) => {
      return c.bhk && c.bhk !== '';
    });
    
    console.log("Filtered configs (only filled rows):", filledConfigs);
    console.log("Number of filled configs:", filledConfigs.length);
    
    return [{
      totalUnits: form.totalUnits,
      minArea: form.minArea,
      maxArea: form.maxArea,
      structure: form.structure,
      // ✅ convert facing object → array
      facing: this.mapFacing(form.facing),

      // ✅ configurations table - only include filled rows
      configurations: filledConfigs.map((c: any) => ({
        facing: c.facing,
        bhk: c.bhk,
        sqft: c.sqft,
        price: c.price,
        units: c.units,
        available: c.available
      }))
    }];
  }

  mapFacing(facing: any): string[] {
    const result: string[] = [];

    if (facing?.east) result.push('East');
    if (facing?.west) result.push('West');
    if (facing?.north) result.push('North');
    if (facing?.south) result.push('South');

    return result;
  }

  mapStructure(structure: any): string[] {
    const result: string[] = [];

    if (structure?.villa) result.push('Villa');
    if (structure?.duplex) result.push('Duplex');
    if (structure?.triplex) result.push('Triplex');
    
    return result;
  }


  validateVilla(form: FormGroup): boolean {
    const phases = form.get('phases') as FormArray;

    // Must have at least one phase
    if (!phases || phases.length === 0) {
      console.warn('Villa validation failed: No phases defined');
      return false;
    }

    return phases.controls.every((phase, pIndex) => {
      const phaseForm = phase as FormGroup;
      
      // ✅ Validate phase-level fields
      const phaseName = phaseForm.get('name')?.value;
      const totalUnits = phaseForm.get('totalUnits')?.value;
      const minArea = phaseForm.get('minArea')?.value;
      const maxArea = phaseForm.get('maxArea')?.value;
      
      if (!phaseName || phaseName.trim() === '') {
        console.warn(`Villa validation failed: Phase ${pIndex} - name is required`);
        return false;
      }
      
      if (!totalUnits || Number(totalUnits) < 1) {
        console.warn(`Villa validation failed: Phase ${pIndex} - totalUnits must be >= 1`);
        return false;
      }
      
      if (minArea === null || minArea === undefined || Number(minArea) < 0) {
        console.warn(`Villa validation failed: Phase ${pIndex} - minArea must be >= 0`);
        return false;
      }
      
      if (maxArea === null || maxArea === undefined || Number(maxArea) < 0) {
        console.warn(`Villa validation failed: Phase ${pIndex} - maxArea must be >= 0`);
        return false;
      }
      
      if (minArea && maxArea && Number(minArea) > Number(maxArea)) {
        console.warn(`Villa validation failed: Phase ${pIndex} - minArea > maxArea`);
        return false;
      }
      
      // ✅ Validate structure checkbox - at least one must be selected
      const structure = phaseForm.get('structure') as FormGroup;
      const hasStructure = structure?.get('villa')?.value || structure?.get('duplex')?.value || structure?.get('triplex')?.value;
      if (!hasStructure) {
        console.warn(`Villa validation failed: Phase ${pIndex} - at least one structure type must be selected`);
        return false;
      }
      
      // ✅ Validate facing checkbox - at least one must be selected
      const facing = phaseForm.get('facing') as FormGroup;
      const hasFacing = facing?.get('east')?.value || facing?.get('west')?.value || facing?.get('north')?.value || facing?.get('south')?.value;
      if (!hasFacing) {
        console.warn(`Villa validation failed: Phase ${pIndex} - at least one facing must be selected`);
        return false;
      }

      const configs = phaseForm.get('configurations') as FormArray;

      // Each phase must have configurations
      if (!configs || configs.length === 0) {
        console.warn(`Villa validation failed: Phase ${pIndex} has no configurations`);
        return false;
      }

      // Filter out empty rows (rows where bhk is empty) - these are newly added empty rows
      const filledConfigs = configs.controls.filter(config => {
        const bhk = config.get('bhk')?.value;
        return bhk && bhk !== '';
      });

      // Must have at least one filled configuration per phase
      if (filledConfigs.length === 0) {
        console.warn(`Villa validation failed: Phase ${pIndex} has no completed configurations`);
        return false;
      }

      return filledConfigs.every((config, cIndex) => {
        const facing = config.get('facing')?.value;
        const bhk = config.get('bhk')?.value;
        const sqft = config.get('sqft')?.value;
        const price = config.get('price')?.value;
        const units = config.get('units')?.value;
        const available = config.get('available')?.value;

        if (!facing || facing === '') {
          console.warn(`Villa validation failed: Phase ${pIndex}, Config ${cIndex} - facing is required`);
          return false;
        }
        
        if (!bhk || bhk === '') {
          console.warn(`Villa validation failed: Phase ${pIndex}, Config ${cIndex} - BHK is required`);
          return false;
        }
        
        if (!sqft || Number(sqft) <= 0) {
          console.warn(`Villa validation failed: Phase ${pIndex}, Config ${cIndex} - sqft must be > 0`);
          return false;
        }
        
        if (!price || Number(price) <= 0) {
          console.warn(`Villa validation failed: Phase ${pIndex}, Config ${cIndex} - price must be > 0`);
          return false;
        }
        
        if (!units || Number(units) <= 0) {
          console.warn(`Villa validation failed: Phase ${pIndex}, Config ${cIndex} - units must be > 0`);
          return false;
        }
        
        if (available === null || available === undefined || Number(available) < 0) {
          console.warn(`Villa validation failed: Phase ${pIndex}, Config ${cIndex} - available must be >= 0`);
          return false;
        }

        return true;
      });
    });
  }

  mapVilla(form: any) {
    const phases = form.phases || [];
    
    return phases.map((p: any) => ({
      name: p.name || '',
      totalUnits: p.totalUnits || '',
      minArea: p.minArea || '',
      maxArea: p.maxArea || '',
      structure: this.mapStructure(p.structure),
      facing: this.mapFacing(p.facing),
      // ✅ Filter out empty rows where bhk is empty
      configurations: (p.configurations || [])
        .filter((c: any) => c.bhk && c.bhk !== '')
        .map((c: any) => ({
          facing: c.facing || '',
          bhk: c.bhk || '',
          sqft: c.sqft || '',
          price: c.price || '',
          units: c.units || '',
          available: c.available || 0
        }))
    }));
  }

  validatePlot(form: FormGroup): boolean {
    const phases = form.get('plotPhases') as FormArray;

    // Must have at least one phase
    if (!phases || phases.length === 0) {
      console.warn('Plot validation failed: No plot phases defined');
      return false;
    }

    return phases.controls.every((phase, pIndex) => {
      const phaseForm = phase as FormGroup;
      
      // ✅ Validate phase-level fields
      const phaseName = phaseForm.get('name')?.value;
      const totalUnits = phaseForm.get('totalUnits')?.value;
      const totalArea = phaseForm.get('totalArea')?.value;
      const plotSizeUnit = phaseForm.get('plotSizeUnit')?.value;
      const minSize = phaseForm.get('minSize')?.value;
      const maxSize = phaseForm.get('maxSize')?.value;
      const length = phaseForm.get('length')?.value;
      const width = phaseForm.get('width')?.value;
      
      if (!phaseName || phaseName.trim() === '') {
        console.warn(`Plot validation failed: Phase ${pIndex} - name is required`);
        return false;
      }
      
      if (!totalUnits || Number(totalUnits) < 1) {
        console.warn(`Plot validation failed: Phase ${pIndex} - totalUnits must be >= 1`);
        return false;
      }
      
      if (totalArea === null || totalArea === undefined || Number(totalArea) <= 0) {
        console.warn(`Plot validation failed: Phase ${pIndex} - totalArea must be > 0`);
        return false;
      }
      
      if (!plotSizeUnit || plotSizeUnit === '') {
        console.warn(`Plot validation failed: Phase ${pIndex} - plotSizeUnit is required`);
        return false;
      }
      
      if (minSize === null || minSize === undefined || Number(minSize) < 0) {
        console.warn(`Plot validation failed: Phase ${pIndex} - minSize must be >= 0`);
        return false;
      }
      
      if (maxSize === null || maxSize === undefined || Number(maxSize) < 0) {
        console.warn(`Plot validation failed: Phase ${pIndex} - maxSize must be >= 0`);
        return false;
      }
      
      if (minSize && maxSize && Number(minSize) > Number(maxSize)) {
        console.warn(`Plot validation failed: Phase ${pIndex} - minSize > maxSize`);
        return false;
      }
      
      if (!length || Number(length) <= 0) {
        console.warn(`Plot validation failed: Phase ${pIndex} - length must be > 0`);
        return false;
      }
      
      if (!width || Number(width) <= 0) {
        console.warn(`Plot validation failed: Phase ${pIndex} - width must be > 0`);
        return false;
      }
      
      // ✅ Validate structure checkbox - at least one must be selected
      const structure = phaseForm.get('structure') as FormGroup;
      const hasStructure = structure?.get('villa')?.value || structure?.get('duplex')?.value || structure?.get('triplex')?.value;
      if (!hasStructure) {
        console.warn(`Plot validation failed: Phase ${pIndex} - at least one structure type must be selected`);
        return false;
      }
      
      // ✅ Validate facing checkbox - at least one must be selected
      const facing = phaseForm.get('facing') as FormGroup;
      const hasFacing = facing?.get('east')?.value || facing?.get('west')?.value || facing?.get('north')?.value || facing?.get('south')?.value;
      if (!hasFacing) {
        console.warn(`Plot validation failed: Phase ${pIndex} - at least one facing must be selected`);
        return false;
      }

      const configs = phaseForm.get('configurations') as FormArray;

      // Each phase must have configurations
      if (!configs || configs.length === 0) {
        console.warn(`Plot validation failed: Phase ${pIndex} has no configurations`);
        return false;
      }

      // Filter out empty rows (rows where facing is empty) - these are newly added empty rows
      const filledConfigs = configs.controls.filter(config => {
        const facing = config.get('facing')?.value;
        return facing && facing !== '';
      });

      // Must have at least one filled configuration
      if (filledConfigs.length === 0) {
        console.warn(`Plot validation failed: Phase ${pIndex} has no completed configurations`);
        return false;
      }

      return filledConfigs.every((config, cIndex) => {
        // Check all required fields with proper type conversion
        const facing = config.get('facing')?.value;
        const plotArea = config.get('plotArea')?.value;
        const sqftPrice = config.get('sqftPrice')?.value;
        const unitPrice = config.get('unitPrice')?.value;
        const units = config.get('units')?.value;
        const available = config.get('available')?.value;

        // Validate facing
        if (!facing || facing === '') {
          console.warn(`Plot validation failed: Phase ${pIndex}, Config ${cIndex} - facing is required`);
          return false;
        }

        // Validate plotArea
        const numPlotArea = Number(plotArea);
        if (plotArea === '' || plotArea === null || plotArea === undefined || numPlotArea <= 0) {
          console.warn(`Plot validation failed: Phase ${pIndex}, Config ${cIndex} - plotArea must be > 0`);
          return false;
        }

        // Validate sqftPrice
        const numSqftPrice = Number(sqftPrice);
        if (sqftPrice === '' || sqftPrice === null || sqftPrice === undefined || numSqftPrice <= 0) {
          console.warn(`Plot validation failed: Phase ${pIndex}, Config ${cIndex} - sqftPrice must be > 0`);
          return false;
        }

        // Validate unitPrice
        const numUnitPrice = Number(unitPrice);
        if (unitPrice === '' || unitPrice === null || unitPrice === undefined || numUnitPrice <= 0) {
          console.warn(`Plot validation failed: Phase ${pIndex}, Config ${cIndex} - unitPrice must be > 0`);
          return false;
        }

        // Validate units
        const numUnits = Number(units);
        if (units === '' || units === null || units === undefined || numUnits <= 0) {
          console.warn(`Plot validation failed: Phase ${pIndex}, Config ${cIndex} - units must be > 0`);
          return false;
        }
        
        // Validate available
        if (available === null || available === undefined || Number(available) < 0) {
          console.warn(`Plot validation failed: Phase ${pIndex}, Config ${cIndex} - available must be >= 0`);
          return false;
        }

        return true;
      });
    });
  }


  mapPlot(form: any) {
    const plotPhases = form.plotPhases || [];
    
    return plotPhases.map((p: any) => ({
      name: p.name || '',
      totalUnits: p.totalUnits || '',
      totalArea: p.totalArea || '',
      plotSizeUnit: p.plotSizeUnit || '',
      minArea: p.minSize || '',
      maxArea: p.maxSize || '',
      length: p.length || '',
      width: p.width || '',
      facing: this.mapFacing(p.facing),
      structure: this.mapStructure(p.structure),
      // ✅ Filter out empty rows where facing is empty
      configurations: (p.configurations || [])
        .filter((c: any) => c.facing && c.facing !== '')
        .map((c: any) => ({
          facing: c.facing || '',
          plotArea: c.plotArea || '',
          sqftPrice: c.sqftPrice || '',
          unitPrice: c.unitPrice || '',
          units: c.units || '',
          available: c.available || 0
        }))
    }));
  }

  validateVenture(form: FormGroup): boolean {
    const phases = form.get('venturePhases') as FormArray;

    // Must have at least one phase
    if (!phases || phases.length === 0) {
      console.warn('Venture validation failed: No venture phases defined');
      return false;
    }

    return phases.controls.every((phase, pIndex) => {
      const phaseForm = phase as FormGroup;
      
      // ✅ Validate phase-level fields
      const phaseName = phaseForm.get('name')?.value;
      const totalUnits = phaseForm.get('totalUnits')?.value;
      const plotSizeUnit = phaseForm.get('plotSizeUnit')?.value;
      const length = phaseForm.get('length')?.value;
      const width = phaseForm.get('width')?.value;
      const minArea = phaseForm.get('minArea')?.value;
      const maxArea = phaseForm.get('maxArea')?.value;
      const totalArea = phaseForm.get('totalArea')?.value;
      
      if (!phaseName || phaseName.trim() === '') {
        console.warn(`Venture validation failed: Phase ${pIndex} - name is required`);
        return false;
      }
      
      if (!totalUnits || Number(totalUnits) < 1) {
        console.warn(`Venture validation failed: Phase ${pIndex} - totalUnits must be >= 1`);
        return false;
      }
      
      if (!plotSizeUnit || plotSizeUnit === '') {
        console.warn(`Venture validation failed: Phase ${pIndex} - plotSizeUnit is required`);
        return false;
      }
      
      if (!length || Number(length) <= 0) {
        console.warn(`Venture validation failed: Phase ${pIndex} - length must be > 0`);
        return false;
      }
      
      if (!width || Number(width) <= 0) {
        console.warn(`Venture validation failed: Phase ${pIndex} - width must be > 0`);
        return false;
      }
      
      if (minArea === null || minArea === undefined || Number(minArea) < 0) {
        console.warn(`Venture validation failed: Phase ${pIndex} - minArea must be >= 0`);
        return false;
      }
      
      if (maxArea === null || maxArea === undefined || Number(maxArea) < 0) {
        console.warn(`Venture validation failed: Phase ${pIndex} - maxArea must be >= 0`);
        return false;
      }
      
      if (minArea && maxArea && Number(minArea) > Number(maxArea)) {
        console.warn(`Venture validation failed: Phase ${pIndex} - minArea > maxArea`);
        return false;
      }
      
      if (!totalArea || Number(totalArea) <= 0) {
        console.warn(`Venture validation failed: Phase ${pIndex} - totalArea must be > 0`);
        return false;
      }
      
      // ✅ Validate structure checkbox - at least one must be selected
      const structure = phaseForm.get('structure') as FormGroup;
      const hasStructure = structure?.get('villa')?.value || structure?.get('duplex')?.value || structure?.get('triplex')?.value;
      if (!hasStructure) {
        console.warn(`Venture validation failed: Phase ${pIndex} - at least one structure type must be selected`);
        return false;
      }
      
      // ✅ Validate facing checkbox - at least one must be selected
      const facing = phaseForm.get('facing') as FormGroup;
      const hasFacing = facing?.get('east')?.value || facing?.get('west')?.value || facing?.get('north')?.value || facing?.get('south')?.value;
      if (!hasFacing) {
        console.warn(`Venture validation failed: Phase ${pIndex} - at least one facing must be selected`);
        return false;
      }

      const configs = phaseForm.get('configurations') as FormArray;

      // Each phase must have configurations
      if (!configs || configs.length === 0) {
        console.warn(`Venture validation failed: Phase ${pIndex} has no configurations`);
        return false;
      }

      // Filter out empty rows (rows where bhk is empty) - these are newly added empty rows
      const filledConfigs = configs.controls.filter(config => {
        const bhk = config.get('bhk')?.value;
        return bhk && bhk !== '';
      });

      // Must have at least one filled configuration
      if (filledConfigs.length === 0) {
        console.warn(`Venture validation failed: Phase ${pIndex} has no completed configurations`);
        return false;
      }

      return filledConfigs.every((config, cIndex) => {
        // Check all required fields with proper type conversion
        const facing = config.get('facing')?.value;
        const villaArea = config.get('villaArea')?.value;
        const bhk = config.get('bhk')?.value;
        const sqFeet = config.get('sqFeet')?.value;
        const sqFeetPrice = config.get('sqFeetPrice')?.value;
        const unitPrice = config.get('unitPrice')?.value;
        const units = config.get('units')?.value;

        // Validate facing
        if (!facing || facing === '') {
          console.warn(`Venture validation failed: Phase ${pIndex}, Config ${cIndex} - facing is required`);
          return false;
        }

        // Validate villaArea
        if (!villaArea || villaArea === '') {
          console.warn(`Venture validation failed: Phase ${pIndex}, Config ${cIndex} - villaArea is required`);
          return false;
        }

        // Validate BHK
        if (bhk === '' || bhk === null || bhk === undefined) {
          console.warn(`Venture validation failed: Phase ${pIndex}, Config ${cIndex} - invalid BHK`);
          return false;
        }

        // Validate sqFeet
        const numSqFeet = Number(sqFeet);
        if (sqFeet === '' || sqFeet === null || sqFeet === undefined || numSqFeet <= 0) {
          console.warn(`Venture validation failed: Phase ${pIndex}, Config ${cIndex} - sqFeet must be > 0`);
          return false;
        }

        // Validate sqFeetPrice
        const numSqFeetPrice = Number(sqFeetPrice);
        if (sqFeetPrice === '' || sqFeetPrice === null || sqFeetPrice === undefined || numSqFeetPrice <= 0) {
          console.warn(`Venture validation failed: Phase ${pIndex}, Config ${cIndex} - sqFeetPrice must be > 0`);
          return false;
        }

        // Validate unitPrice
        const numUnitPrice = Number(unitPrice);
        if (unitPrice === '' || unitPrice === null || unitPrice === undefined || numUnitPrice <= 0) {
          console.warn(`Venture validation failed: Phase ${pIndex}, Config ${cIndex} - unitPrice must be > 0`);
          return false;
        }

        // Validate units
        const numUnits = Number(units);
        if (units === '' || units === null || units === undefined || numUnits <= 0) {
          console.warn(`Venture validation failed: Phase ${pIndex}, Config ${cIndex} - units must be > 0`);
          return false;
        }

        return true;
      });
    });
  }

  mapVenture(form: any) {
    const venturePhases = form.venturePhases || [];
    
    return venturePhases.map((p: any) => ({
      name: p.name || '',
      totalUnits: p.totalUnits || '',
      plotSizeUnit: p.plotSizeUnit || '',
      length: p.length || '',
      width: p.width || '',
      minArea: p.minArea || '',
      maxArea: p.maxArea || '',
      totalArea: (p.totalArea || '') + ' Acre',
      structure: this.mapStructure(p.structure),
      facing: this.mapFacing(p.facing),
      // ✅ Filter out empty rows where bhk is empty
      configurations: (p.configurations || [])
        .filter((c: any) => c.bhk && c.bhk !== '')
        .map((c: any) => ({
          facing: c.facing || '',
          villaArea: c.villaArea || '',
          bhk: c.bhk || '',
          sqFeet: c.sqFeet || '',
          sqFeetPrice: c.sqFeetPrice || '',
          unitPrice: c.unitPrice || '',
          units: c.units || ''
        }))
    }));
  }

  loadSavedConfiguration() {
    console.log('Loading saved configuration...');
    const saved = this.lookupModel.getVendorProperty() || null;

    if (!saved || !saved.configuration) return;

    // Clear existing
    this.configurations.clear();
    this.towers.clear();
    this.phases.clear();
    this.plotPhases.clear();
    this.venturePhases.clear();

    const subCategory = this.selectedSubCategory?.subCategoryName;
    console.log('Sub-category detected:', subCategory);

    // ✅ Independent House
    if (subCategory?.includes('Independent')) {
      console.log('Loading Independent House configuration...');
      // Independent House returns [{ ... }] array, so get the first element
      const config = Array.isArray(saved.configuration) ? saved.configuration[0] : saved.configuration;
      
      if (config) {
        this.propertyForm.patchValue({
          totalUnits: config.totalUnits,
          minArea: config.minArea,
          maxArea: config.maxArea,
          structure: config.structure,
          facing: {
            east: config?.facing?.includes('East') || false,
            west: config?.facing?.includes('West') || false,
            north: config?.facing?.includes('North') || false,
            south: config?.facing?.includes('South') || false
          }
        });
        
        (config.configurations || []).forEach((c: any) => {
          const row = this.createConfigRow();
          row.patchValue({
            facing: c.facing || '',
            bhk: c.bhk,
            sqft: c.sqft,
            price: c.price,
            units: c.units,
            available: c.available || 0
          });
          this.configurations.push(row);
        });
      }
    }

    // ✅ Apartment (Towers)
    else if (subCategory?.includes('Apartment')) {
      console.log('Loading Apartment configuration...');
      saved.configuration.forEach((t: any) => {
        const tower = this.createTower(t.name);
        tower.patchValue({
          name: t.name,
          floors: t.floors || '',
          totalUnits: t.totalUnits || '',
          minArea: t.minArea || '',
          maxArea: t.maxArea || '',
          pricePerSqft: t.pricePerSqft || '',
          facing: {
            east: t.facing?.includes('East') || false,
            west: t.facing?.includes('West') || false,
            north: t.facing?.includes('North') || false,
            south: t.facing?.includes('South') || false
          }
        });

        const configs = tower.get('configurations') as FormArray;
        configs.clear();

        (t.configurations || []).forEach((c: any) => {
          const row = this.createConfigRow();
          row.patchValue({
            facing: c.facing || '',
            bhk: c.bhk,
            sqft: c.sqft,
            price: c.price,
            units: c.units,
            available: c.available || 0
          });
          configs.push(row);
        });

        this.towers.push(tower);
      });
    }

    // ✅ Villa (Phases)
    else if (subCategory?.includes('Villa')) {
      console.log('Loading Villa configuration...');
      saved.configuration.forEach((p: any) => {
        const phase = this.createPhase(p.name);
        phase.patchValue({
          name: p.name,
          totalUnits: p.totalUnits || '',
          minArea: p.minArea || '',
          maxArea: p.maxArea || '',
          structure: {
            villa: p.structure?.includes('Villa') || false,
            duplex: p.structure?.includes('Duplex') || false,
            triplex: p.structure?.includes('Triplex') || false
          },
          facing: {
            east: p.facing?.includes('East') || false,
            west: p.facing?.includes('West') || false,
            north: p.facing?.includes('North') || false,
            south: p.facing?.includes('South') || false
          }
        });

        const configs = phase.get('configurations') as FormArray;
        configs.clear();

        (p.configurations || []).forEach((c: any) => {
          const row = this.createConfigRow();
          row.patchValue({
            facing: c.facing || '',
            bhk: c.bhk,
            sqft: c.sqft,
            price: c.price,
            units: c.units,
            available: c.available || 0
          });
          configs.push(row);
        });

        this.phases.push(phase);
      });
    }

    // ✅ Venture (Check before Plot since "Venture Plot" contains "Plot")
    else if (subCategory?.includes('Venture')) {
      console.log('Loading Venture configuration...');
      saved.configuration.forEach((p: any) => {
        const phase = this.createVenturePhase(p.name);
        phase.patchValue({
          name: p.name,
          totalUnits: p.totalUnits || '',
          plotSizeUnit: p.plotSizeUnit || '',
          length: p.length || '',
          width: p.width || '',
          minArea: p.minArea || '',
          maxArea: p.maxArea || '',
          totalArea: p.totalArea || '',
          structure: {
            villa: p.structure?.includes('Villa') || false,
            duplex: p.structure?.includes('Duplex') || false,
            triplex: p.structure?.includes('Triplex') || false
          },
          facing: {
            east: p.facing?.includes('East') || false,
            west: p.facing?.includes('West') || false,
            north: p.facing?.includes('North') || false,
            south: p.facing?.includes('South') || false
          }
        });

        const configs = phase.get('configurations') as FormArray;
        configs.clear();

        (p.configurations || []).forEach((c: any) => {
          const row = this.createVentureRow();
          row.patchValue({
            facing: c.facing || '',
            villaArea: c.villaArea || '',
            bhk: c.bhk || '',
            sqFeet: c.sqFeet || '',
            sqFeetPrice: c.sqFeetPrice || '',
            unitPrice: c.unitPrice || '',
            units: c.units || ''
          });
          configs.push(row);
        });

        this.venturePhases.push(phase);
      });
    }

    // ✅ Plot (Society Plot, Other Plot)
    else if (subCategory?.includes('Plot')) {
      console.log('Loading Plot configuration...');
      saved.configuration.forEach((p: any) => {
        const phase = this.createPlotPhase(p.name);
        phase.patchValue({
          name: p.name,
          totalUnits: p.totalUnits || '',
          totalArea: p.totalArea || '',
          plotSizeUnit: p.plotSizeUnit || '',
          minSize: p.minArea || '',
          maxSize: p.maxArea || '',
          length: p.length || '',
          width: p.width || '',
          structure: {
            villa: p.structure?.includes('Villa') || false,
            duplex: p.structure?.includes('Duplex') || false,
            triplex: p.structure?.includes('Triplex') || false
          },
          facing: {
            east: p.facing?.includes('East') || false,
            west: p.facing?.includes('West') || false,
            north: p.facing?.includes('North') || false,
            south: p.facing?.includes('South') || false
          }
        });

        const configs = phase.get('configurations') as FormArray;
        configs.clear();

        (p.configurations || []).forEach((c: any) => {
          const row = this.createPlotRow();
          row.patchValue({
            facing: c.facing || '',
            plotArea: c.plotArea || '',
            sqftPrice: c.sqftPrice || '',
            unitPrice: c.unitPrice || '',
            units: c.units || '',
            available: c.available || 0
          });
          configs.push(row);
        });

        this.plotPhases.push(phase);
      });
    }
  }




  submit(formvalue: any): void {
    this.isSubmitted = true;
    console.log('Submit clicked. Form value:', formvalue);
    console.log('Form valid:', this.propertyForm.valid);
    console.log('Form errors:', this.propertyForm.errors);

    if(!this.propertyForm.valid) {
      console.log(this.propertyForm)
      alert("getting errors")
      return;
    }

    // ✅ Note: We skip the generic propertyForm.invalid check because it includes
    // empty FormArray rows that haven't been filled yet. These are valid to leave empty.
    // The subcategory-specific validators (validateIndependent, validateApartment, etc)
    // correctly filter out empty rows and validate only filled configurations.

    // Get the subcategory configuration handler
    const subCategory = this.showIndependentConfig as any;
    console.log('Selected subcategory:', subCategory);
    
    const configHandler = this.SUBCATEGORY_CONFIG[subCategory];

    if (!configHandler) {
      console.error('Unsupported sub-category configuration:', subCategory);
      alert('Unsupported sub-category: ' + subCategory);
      return;
    }

    // ✅ Validate subcategory-specific data
    if (!configHandler.validate(this.propertyForm)) {
      console.warn('Subcategory validation failed for:', subCategory);
      alert('Please complete all required fields for ' + subCategory);
      return;
    }

    // ✅ Map the data if validation passes
    const configData = configHandler.map(formvalue);
    console.log('Mapped Config Data :: ', configData);

    // Save configuration to lookup model
    let propertyInfo = this.lookupModel.getVendorProperty() || {};
    propertyInfo.configuration = configData;
    propertyInfo.measurementId = this.selectedMeasurement?.measurementId;
    propertyInfo.categoryId = this.selectedCategory?.categoryId;
    propertyInfo.subCategoryId = this.selectedSubCategory?.subCategoryId;
    propertyInfo.moduleId = this.selectedModule?.moduleId;
    propertyInfo.projectName = this.projectName?.value;
    propertyInfo.projectDescription = this.projectDescription?.value;
    propertyInfo.companyId = this.selectedCompany?.companyId;
    propertyInfo.projectStatus = this.projectStatus;

    this.lookupModel.setVendorProperty(propertyInfo);
    console.log('Configuration saved. Navigating to next page...');
    this.showNextPage.emit(2);
  }


  get moduleId() { return this.propertyForm?.get('moduleId'); }
  get measurement() { return this.propertyForm?.get('measurement'); }
  get categoryId() { return this.propertyForm?.get('categoryId'); }
  get subCategoryId() { return this.propertyForm?.get('subCategoryId'); }

  get projectName() { return this.propertyForm?.get('projectName'); }
  get projectDescription() { return this.propertyForm?.get('projectDescription'); }

  // ✅ Convert saved facing array back to object format
  convertFacingArrayToObject(facingData: any): any {
    if (!facingData) return { east: false, west: false, north: false, south: false };
    
    // If it's already an object with boolean values, return it
    if (typeof facingData === 'object' && !Array.isArray(facingData)) {
      return {
        east: facingData.east === true || facingData.east === 'true',
        west: facingData.west === true || facingData.west === 'true',
        north: facingData.north === true || facingData.north === 'true',
        south: facingData.south === true || facingData.south === 'true'
      };
    }
    
    // If it's an array of strings like ['East', 'West', ...], convert to object
    if (Array.isArray(facingData)) {
      return {
        east: facingData.includes('East'),
        west: facingData.includes('West'),
        north: facingData.includes('North'),
        south: facingData.includes('South')
      };
    }
    
    return { east: false, west: false, north: false, south: false };
  }

  // ✅ Convert saved structure array back to object format
  convertStructureArrayToObject(structureData: any): any {
    if (!structureData) return { villa: false, duplex: false, triplex: false };
    
    // If it's already an object with boolean values, return it
    if (typeof structureData === 'object' && !Array.isArray(structureData)) {
      return {
        villa: structureData.villa === true || structureData.villa === 'true',
        duplex: structureData.duplex === true || structureData.duplex === 'true',
        triplex: structureData.triplex === true || structureData.triplex === 'true'
      };
    }
    
    // If it's an array of strings like ['Villa', 'Duplex', ...], convert to object
    if (Array.isArray(structureData)) {
      return {
        villa: structureData.includes('Villa'),
        duplex: structureData.includes('Duplex'),
        triplex: structureData.includes('Triplex')
      };
    }
    
    return { villa: false, duplex: false, triplex: false };
  }

}

